/** English translations */
import type { TranslationDict } from "./tr";

const en: TranslationDict = {
  common: {
    privacyPre: "Your personal data is processed as described in our ",
    privacyLink: "Privacy Notice",
    termsLink: "Terms of Use",
    privacyPost: ".",
    all: "All",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    adding: "Adding...",
    create: "Create",
    creating: "Creating...",
    close: "Close",
    /** Belge yazımı reddedildi (409 stale_write): veri tazelendi. */
    staleWrite: "This record changed elsewhere while you were editing. The latest version has been loaded — check your change and save again.",
    back: "Back",
    loading: "Loading...",
    loadMore: "Load more",
    perPage: "per page",
    logout: "Logout",
    switchToLight: "Switch to light mode",
    switchToDark: "Switch to dark mode",
    error: "Error",
    unknownError: "Unknown error",
    errorOccurred: "An error occurred",
    confirm: "Confirm",
    phone: "Phone",
    email: "Email",
    name: "Name",
    date: "Date",
    actions: "Actions",
    yes: "Yes",
    no: "No",
    none: "None",
    other: "Other",
    total: "Total",
    language: "Language",
    uploading: "Uploading...",
    upload: "Upload",
    skip: "Skip",
    chooseFile: "Choose file",
    remove: "Remove",
    help: "Help",
    left: "left",
    more: "More",
    otherPages: "Other Pages",
    optional: "optional",
  },
  nav: {
    dashboard: "Dashboard",
    guests: "Guests",
    sms: "SMS",
    moderation: "Moderation",
    organizers: "Organizers",
    users: "Users",
    weddings: "Weddings",
    myWeddings: "My Weddings",
    profile: "My Profile",
    home: "Home",
    rsvp: "RSVP",
    upload: "Upload",
    gallery: "Gallery",
    slideshow: "Stage",
    reji: "Reji",
    analytics: "QR Analytics",
    allWeddings: "All Weddings",
    backToWeddings: "Back to Weddings",
    organizer: "Organizer",
    maintenance: "Error Tracking",
    controlCenter: "Control Center",
    smsCenter: "SMS Center",
    site: "Sales & Site",
    privacy: "Privacy requests",
    print: "Print",
    themes: "Themes",
    settings: "Settings",
    packages: "My packages",
  },
  auth: {
    welcomeTitle: "Welcome to Our Wedding",
    loginTitle: "Login",
    loginDesc: "We will send a verification code via SMS",
    phoneLabel: "Phone",
    login: "Log In",
    verifyTitle: "Verification Code",
    verifyDesc: "Enter the 6-digit code sent to your phone",
    codeLabel: "Code",
    verify: "Verify",
    phonePlaceholder: "+1 XXX XXX XXXX",
    loggingIn: "Logging in...",
    enterCode: "Enter the 6-digit code sent to your phone",
    verifying: "Verifying...",
    wrongNumber: "Change number",
    changeNumber: "Change number",
    turnstileRequired: "Please complete the verification check",
    codeFailed: "Code verification failed",
    logoutLabel: "Log out",
    codePlaceholder: "123456",
    otpUnavailable: "SMS verification is currently unavailable. Please contact the organizer.",
    invalidPhone: "Enter a valid phone number — a leading 0 is fine.",
    countrySelectLabel: "Country code",
    countrySearchPlaceholder: "Search country…",
    noCountryResults: "No countries found",
    phoneHint: "Type it with or without a leading 0 — we'll fix it",
    banned: "You have been suspended from this wedding. Please contact the organizer.",
    celebrationPrefix: "A celebration of love:",
    tabLogin: "Log in",
    tabRegister: "Register",
    registerTitle: "Register",
    registerDesc: "Enter your name and phone number to join the wedding.",
    alreadyRegisteredTitle: "Already registered",
    alreadyRegisteredDesc: "This number is already registered — just log in to continue.",
    alreadyRegisteredSwitch: "Log in",
  },
  organizerAuth: {
    signupClosed: "There is no organizer account with this email. New accounts are currently opened after a quote — request one from the form at wedding.mertbal.com.",
    title: "Organizer Login",
    emailPrompt: "Enter your email address",
    codePrompt: "Enter the code sent to your email",
    emailLabel: "Email",
    sendCode: "Send Code",
    verifyCode: "Verification Code",
    login: "Log In",
    changeEmail: "Use different email",
    emailPlaceholder: "organizer@example.com",
    panelTitle: "Organizer Panel",
    tagline: "Manage your weddings",
    welcomeBack: "Welcome back",
    verifyTitle: "Verify your identity",
    verifyHint: "If an account exists for {{email}}, we sent a 6-digit code. If not, that address gets an info email instead.",
    signupVerifyHint: "We sent the code to {{email}} — enter it to open your account.",
    noAccount: "No account yet?",
    signupCta: "Create an account",
    quoteCta: "Get a quote",
    haveAccount: "Already have an account?",
    loginCta: "Sign in",
    signupTitle: "Create an account",
    signupPrompt: "Enter your name and email; your account opens with the code we email you.",
    nameLabel: "Full name",
    namePlaceholder: "Jane Doe",
    acceptTerms: "Terms of use",
    acceptAnd: " and the ",
    acceptKvkk: "privacy notice",
    acceptPost: " — I have read and accept them.",
    createAccount: "Create account",
    signupMissing: "Enter your name and accept the terms.",
    signupClosedTitle: "New accounts are opened by quote",
    signupClosedBody: "Fill in the form on our website for pricing and details; we’ll open your account.",
    disposableEmail: "This email address isn’t accepted. Please use a permanent address.",
    emailAliasExists: "An account already exists for this mailbox (the same address with dots or a +tag). Please sign in with your existing address.",
    tooManySignups: "Too many sign-ups from this network today. Please try again later.",
    accountNotFound: "No account is linked to this email, or the sign-up expired. Please try again.",
    secureGateway: "Secure Organizer Gateway",
  },
  dashboard: {
    title: "Dashboard",
    totalGuests: "Total Guests",
    attending: "Attending",
    people: "people",
    maybe: "Maybe",
    notAttending: "Not Attending",
    pendingMedia: "Pending Media",
    approvedMedia: "Approved Media",
    welcomeBack: "Welcome,",
    activityFeed: "Recent Activity",
    quickActions: "Quick Actions",
    daysLeft: "days to go",
    weddingPast: "What a day!",
    inviteLink: "Invite Link",
    inviteLinkHint: "Share this link with guests to join your wedding.",
    copied: "Copied!",
    eventStatus: "Event Status",
    statusOn: "On",
    statusOff: "Off",
    statusScheduled: "Scheduled",
    recentMedia: "Recent Photos",
    noMediaYet: "No approved photos yet",
    capacityUsed: "Capacity used",
    pendingAlert: "photos awaiting review",
    pendingAlertSub: "Tap to open moderation queue",
    quickGuestsSub: "Manage guest list",
    quickModerationSub: "Review pending uploads",
    quickGallerySub: "Browse approved media",
    quickSettingsSub: "Wedding settings",
    noActivity: "No recent activity",
    qrFunnelTitle: "QR Funnel",
    qrScans: "Scans",
    qrRegistrations: "Registrations",
    qrConversion: "Conversion",
    qrFunnelEmpty: "No scans yet — share your QR code",
    qrFunnelDetail: "Details",
  },
  analytics: {
    title: "QR Analytics",
    summary: "Summary",
    scans: "Scans",
    unique: "Unique",
    registrations: "Registrations",
    conversion: "Conversion",
    byDay: "Time",
    byDevice: "Device",
    byLocation: "Location",
    funnel: "Funnel",
    unknown: "Unknown",
    exportCsv: "Download CSV",
    empty: "No scans yet — share your QR code",
    csvOk: "CSV downloaded",
    csvErr: "Download failed",
    peak: "Peak",
    share: "share",
    deviceMobile: "Mobile",
    deviceTablet: "Tablet",
    deviceDesktop: "Desktop",
    refresh: "Refresh",
    exportPdf: "Download report",
    reportTitle: "QR Analytics Report",
    peakDay: "Peak day",
    generatedAt: "Generated",
    pdfOk: "Report downloaded",
    pdfErr: "Could not generate report",
  },
  guests: {
    title: "Guests",
    titleCount: "Guests ({{count}})",
    phoneRequired: "Phone *",
    namePlaceholder: "John Doe",
    importBtn: "Import",
    exportBtn: "Export",
    jsonFormat: "JSON format:",
    columnPhone: "Phone",
    columnName: "Name",
    columnRsvp: "RSVP",
    columnHeadcount: "Count",
    columnDiet: "Diet",
    columnNotes: "Notes",
    columnEditedBy: "Edited by",
    columnDate: "Date",
    dietNone: "None",
    dietVegan: "Vegan",
    dietVegetarian: "Vegetarian",
    dietGlutenFree: "Gluten-free",
    dietOther: "Other",
    deleteConfirm: 'Are you sure you want to delete "{{name}}"?',
    importSuccess: "{{imported}} guests added, {{skipped}} skipped (total: {{total}})",
    importJsonError: "JSON file must be an array or { guests: [...] } format",
    added: "Guest added",
    deleted: "Guest deleted",
    deleteNoMedia: "The guest will be removed from the list. They have no uploads.",
    deleteMediaKept: "The guest will be removed from the list. Their {{count}} media item(s) STAY in the gallery — tick the box below to delete them too.",
    deleteMediaToo: "Also delete their {{count}} media item(s)",
    deleteMediaTooHint: "Deleted media can be recovered from Moderation → Deleted for {{days}} days, then it is removed permanently.",
    saved: "Changes saved",
    exported: "{{count}} guests exported",
    phoneHint: "Include country code — e.g. +905551234567",
    phoneInvalid: "Invalid number — include country code (e.g. +1...)",
    nameRequired: "Name is required",
    banGuest: "Ban Guest",
    smsConsentOn: "SMS consent on — click to remove",
    smsConsentOff: "No SMS consent — click to add",
    smsOptedOut: "Guest opted out of SMS",
    smsConsentSet: "SMS consent added",
    smsConsentUnset: "SMS consent removed",
    banDuration1d: "1 Day",
    banDuration7d: "7 Days",
    banDuration30d: "30 Days",
    banReasonPlaceholder: "Reason (optional)",
    banPermanent: "Permanent",
    banUntil: "Banned — until {{date}}",
    banPermanentLabel: "Permanently Banned",
    unban: "Remove Ban",
    banned: "Banned",
    guestBanned: "Guest banned",
    guestUnbanned: "Ban removed",
    addGuest: "Add Guest",
    editTitle: "Edit Guest",
    filterAll: "All",
    filterNone: "No RSVP",
    rsvpYes: "Confirmed",
    rsvpNo: "Declined",
    rsvpMaybe: "Maybe",
    rsvpYesCount: "{{count}} attending",
    rsvpNoCount: "{{count}} declining",
    rsvpMaybeCount: "{{count}} maybe",
    noGuests: "No guests yet",
    noGuestsSearch: "No guests match your search",
    noName: "No name",
    searchPlaceholder: "Search by name or phone…",
    columnDetails: "Details",
    headcountLabel: "{{count}} people",
    headcountMax: "Max {{max}}",
    headcountPerson: "people",
    timeMinutes: "{{count}}m",
    timeHours: "{{count}}h",
    timeDays: "{{count}}d",
    diet_vegan: "Vegan",
    diet_vegetarian: "Vegetarian",
    diet_gluten_free: "Gluten-free",
    diet_other: "Special",
  },
  moderation: {
    title: "Media Moderation",
    titleCount: "Media Moderation ({{count}} pending)",
    noItems: "No pending media",
    approve: "Approve",
    reject: "Reject",
    reviewedBy: "Reviewed by: {{email}}",
    history: "History",
    pending: "Pending",
    returnToPending: "Return to Review",
    autoRefreshOff: "Auto-refresh off",
    refreshNow: "Refresh",
    autoRefreshOn: "Auto-refresh on ({{s}}s)",
    bulkApprove: "Approve Selected",
    bulkReject: "Reject Selected",
    bulkApproved: "Selected items approved",
    bulkRejected: "Selected items rejected",
    rejectDeleteHint: "Rejected — file will be permanently deleted in 7 days",
    bulkRejectedDeleteHint: "Rejected — files will be permanently deleted in 7 days",
    deletesIn: "Deletes in {{days}}d",
    deletesInHours: "Deletes in {{hours}}h",
    deletesImminently: "Deleting soon",
    selected: "{{count}} selected",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    tabApproved: "Approved",
    tabRejected: "Rejected",
    tabDeleted: "Deleted",
    noDeleted: "No deleted media",
    deletedHint: "These items will be permanently deleted in {{days}} days. Press Restore to recover them.",
    restore: "Restore",
    guestRemovedLocked: "Removed by guest",
    guestRemovedLockedHint: "The guest removed this themselves. The decision is theirs: it can't be restored or approved and is permanently deleted when the period ends.",
    restored: "Media restored",
    guestDeleted: "removed guest",
    unknownGuest: "Unknown guest",
  },
  wedding: {
    title: "Weddings",
    myWeddings: "My Weddings",
    newWedding: "New Wedding",
    createTitle: "Create New Wedding",
    slugPlaceholder: "URL slug (e.g. john-jane)",
    titlePlaceholder: "Wedding title",
    datePlaceholder: "Date and time",
    locationPlaceholder: "Location",
    created: "Wedding created",
    createFailed: "Failed to create wedding",
    deleteConfirm: 'Are you sure you want to delete "{{slug}}"? This cannot be undone.',
    deleteFailed: "Delete failed",
    deleteButton: "Delete wedding",
    archiveConfirm: 'Are you sure you want to archive "{{slug}}"? It will be removed from your list but data is preserved. It still counts against your plan limit.',
    archived: "Wedding archived",
    archiveFailed: "Archive failed",
    archiveButton: "Archive wedding",
    unarchived: "Wedding restored",
    unarchiveFailed: "Restore failed",
    unarchiveButton: "Restore wedding",
    archivedSection: "Archived weddings",
    archivedBadge: "Archived",
    noWeddings: "You don't have any weddings yet.",
    noWeddingsHint: 'Create your first wedding using the "New Wedding" button above.',
    notFound: "Wedding Not Found",
    noWeddingsAdmin: "No weddings created yet",
    owner: "Owner",
    enterTitle: "Please enter a wedding title",
    enterDate: "Please enter a wedding date and time",
    enterLocation: "Please enter a wedding location",
    descriptionPlaceholder: "Description (optional)",
    infoTab: "Wedding Details",
    registrationTab: "Registration",
    bgTab: "Backgrounds",
    notificationsTab: "Notifications",
    slugReadOnly: "Slug is permanent and cannot be changed",
    settingsHint: "Manage your wedding details and guest registration settings.",
    updateSuccess: "Wedding details saved",
    mainEventLabel: "Wedding",
    event2: {
      sectionTitle: "Second Event",
      sectionHint: "Add an extra event such as a henna night, civil ceremony or after-party.",
      addButton: "Add Second Event",
      removeButton: "Remove",
      dateTimeLabel: "Date and Time",
      locationLabel: "Venue",
      locationPlaceholder: "Venue name or address",
      labelLabel: "Event Type",
      customLabelPlaceholder: "Enter the event name",
      dateRequired: "Date and time are required for the second event",
      customLabelRequired: "Event name is required when 'Other' is selected",
    },
    program: {
      programTitle: "Programme",
      daysLeft: "{{count}} DAYS TO GO",
      today: "TODAY",
      past: "COMPLETED",
      tomorrow: "TOMORROW",
      mainEventTypeLabel: "Main Event Type",
    },
    event2Label: {
      kina: "Henna Night",
      nikah: "Civil Ceremony",
      dugun: "Wedding",
      afterParty: "After Party",
      other: "Other",
    },
    updateFailed: "Update failed",
    eventDateLocked: "The event date has passed — it can no longer be changed (a package covers one event). Contact support if it needs correcting.",
    bgSectionTitle: "Background Images",
    bgSlotAuth: "Guest Login Background",
    bgSlotCountdown: "Countdown Background",
    bgSlotRsvp: "RSVP Background",
    bgUploaded: "Background uploaded",
    bgDeleted: "Background deleted",
    bgUploadFailed: "Upload failed",
    bgDeleteFailed: "Delete failed",
    language: "Guest App Language",
    languageHint: "Sets the default country code on the guest login page.",
    languageSaved: "Language saved",
    languageSaveFailed: "Failed to save language",
    bride: "Bride",
    groom: "Groom",
    enterBride: "Please enter the bride's name",
    enterGroom: "Please enter the groom's name",
    manage: "Manage",
    slugAvailable: "Available",
    slugTaken: "Already taken",
    slugChecking: "Checking...",
    slugInvalidFormat: "Invalid (min. 3 chars, a-z 0-9 hyphens only)",
    nameLettersOnly: "Letters only please",
    locationInvalid: "Contains invalid characters",
    pkgWithGuests: "{{plan}} · up to {{n}} guests",
    pkgTrial: "{{plan}} (trial) · {{n}} guests",
    pkgAccountWide: "{{plan}} · account-wide · {{n}} guests",
    createAccountWideNotice: "This wedding opens without a package and uses your account's {{plan}} plan (up to {{n}} guests).",
    applyTo: "Choose a wedding",
    applyButton: "Assign to this wedding",
    applied: "Package assigned to “{{title}}”.",
    applyFailed: "Couldn’t assign the package, please try again.",
    packageUnavailable: "This package is no longer available (it may be assigned to another wedding). The list was refreshed.",
    packageForWedding: "Package for this wedding",
    packageChoiceHint: "Each package is for one wedding; the wedding's limits are the chosen package's limits.",
    freeSlotsFull: "You have no available package. Buy a package for a new wedding.",
    createdWithPackage: "Wedding created and your package assigned to it.",
    pkgFree: "{{plan}} · {{n}} guests",
    pkgLegacy: "{{plan}} (transition) · {{n}} guests",
    pkgSuspended: "No package — wedding suspended",
    walletAvailable: "Available packages",
    walletNone: "None",
    walletInUse: "In use: {{n}}",
    walletPending: "Awaiting payment: {{n}}",
    walletLink: "My packages →",
    buyPackage: "Buy a package",
    noPackageHint: "You have no available package for a new wedding. Buy one; it appears here once your payment is confirmed.",
    packagePendingOption: "{{pkg}} · {{number}} — awaiting payment",
    createFreeNotice: "This wedding opens with the {{plan}} package (up to {{n}} guests). You can upgrade it later by assigning a paid package; the Free package is then used up.",
    startConsent: "I agree that the service starts immediately, before the withdrawal period ends, and I understand that I have no right of withdrawal once the package is assigned to this wedding.",
    packageRequired: "Choose a package for this wedding.",
    consentRequired: "Tick the consent box for a paid package.",
    upgradeTitle: "Assign an available package to a wedding",
    upgradeHint: "A paid package upgrades a wedding on the Free or a transition package (the Free package is then used up). To change it after assigning, contact support.",
    alreadyPaid: "This wedding already has a paid package.",
    weddingHasPackage: "The Free package can only be assigned to a wedding without a package (suspended).",
  },
  profile: {
    title: "My Profile",
    emailLabel: "Email",
    nameLabel: "Full Name",
    phoneLabel: "Phone",
    phoneLocked: "Contact support to change your phone number.",
    memberSince: "Member since {{date}}",
    loadFailed: "Failed to load profile",
    updateSuccess: "Profile updated",
    updateFailed: "Update failed",
    deactivateTitle: "Deactivate Account",
    deactivateDesc: "Your account will be suspended. It reactivates automatically when you log in again via OTP.",
    deactivateConfirm: "Yes, Deactivate",
    deactivateSuccess: "Account deactivated. Redirecting...",
    deactivateFailed: "Failed to deactivate account",
    dangerZone: "Danger Zone",
    accountInfo: "Account Information",
    phoneChangeTitle: "Phone Number Change",
    phoneChangeAction: "Change",
    phoneVerified: "Verified",
    phoneNotVerified: "Not verified (required for test SMS)",
    phoneVerifyAction: "Verify",
    phoneVerifyTitle: "Verify phone",
    phoneVerifyInfo: "Enter the 6-digit code sent to {{phone}}.",
    phoneVerifyResend: "Resend",
    phoneVerifySubmit: "Verify",
    phoneVerifySuccess: "Phone verified",
    phoneChangeInfo: "A request will be created to change your phone number. Our team will process it as soon as possible.",
    phoneChangeSubject: "Phone number change request",
    phoneCurrent: "Current",
    phoneChangeNew: "New",
    phoneNone: "None",
    phoneChangeAutoNote: "This request was automatically created from the profile page.",
    phoneChangeSubmit: "Submit Request",
    phoneChangeSuccess: "Request submitted",
    phoneChangeSuccessDesc: "Our team will verify and update your number shortly.",
    phoneChangeViewTicket: "View Ticket",
    phoneChangeFailed: "Failed to submit request",
    phoneChangeRequired: "Please enter a new phone number",
    phoneChangeFormat: "Number must start with country code (e.g. +14155551234)",
    phoneChangeSame: "New number is the same as the current one",
    accountPackages: "Account & packages",
    packagesExplain: "Every wedding is tied to a package. Your account has one Free package; every paid order is a new package. Your packages and order history are in My packages.",
    accountWideBadge: "Account-wide: {{plan}}",
    accountWideTitle: "Account-wide plan",
    accountWideExplain: "Your account's own {{plan}} plan applies to weddings without an assigned package. Paid packages are for one wedding each and set that wedding's limits.",
    orderLegacy: "Transition (old account plan)",
    myOrders: "My orders",
    noOrders: "No orders yet.",
    orderPkg: "{{plan}} · up to {{n}} guests",
    orderWedding: "Wedding: {{title}}",
    orderCancelled: "Cancelled",
    orderStatus: {
      pending: "Awaiting payment",
      paid: "Paid",
      cancelled: "Cancelled",
    },
    buyPackage: "Buy a package for a new wedding",
    freeTitle: "Free package",
    freeStatus: {
      available: "Available — you can open a new wedding",
      assigned: "In use: {{title}}",
      ended: "Used up — the wedding was upgraded to a paid package",
      used: "Used",
    },
    packagesLink: "My packages",
  },
  rsvp: {
    title: "RSVP",
    welcomeGuest: "Welcome, {{name}}",
    shareStatus: "Let us know if you'll attend",
    formTitle: "Attendance Form",
    formDescUpdate: "You can update your information",
    formDescNew: "Please fill in your details",
    statusLabel: "Attendance Status",
    statusYes: "Attending",
    statusMaybe: "Maybe",
    statusNo: "Not Attending",
    headcountLabel: "Number of Guests",
    decreaseHeadcount: "Decrease guest count",
    increaseHeadcount: "Increase guest count",
    dietLabel: "Dietary Preference",
    dietNone: "No restrictions",
    dietVegan: "Vegan",
    dietVegetarian: "Vegetarian",
    dietGlutenFree: "Gluten-free",
    dietOther: "Other",
    notesLabel: "Notes (optional)",
    notesPlaceholder: "Any message you'd like to share...",
    submitting: "Submitting...",
    saved: "Saved!",
    update: "Update",
    submit: "Submit",
    successUpdate: "Your response has been updated!",
    successNew: "Your response has been submitted!",
    thanks: "Thank you",
  },
  media: {
    uploadTitle: "Upload Photo / Video",
    uploadDesc: "Share your memories with us",
    heicLocationNote: "Location data is removed from photos before upload, but this isn't possible for HEIC photos. If you don't want your location included, choose the photo as JPEG or turn off location in your camera.",
    uploadCard: "Upload Media",
    supportedFormats: "JPEG, PNG, HEIC, MP4, MOV files supported",
    selectFile: "Select a file or take a photo",
    preview: "Preview",
    uploadBtn: "Upload",
    unsupportedType: "Unsupported file type",
    fileTooLarge: "File too large (max {{max}} MB)",
    uploadFailed: "Failed to upload file",
    uploadPartialError: "Some files failed to upload.",
    errorTooLarge: "{{name}} is too large (max {{limit}} MB)",
    errorUnsupportedType: "{{name}} is not supported (JPEG, PNG, HEIC, MP4, MOV)",
    videoLongWarning: "{{name}} is about {{duration}}s long — long videos can struggle to upload on weak connections, keeping it short is recommended.",
    retryFailed: "Retry failed",
    dropzoneDesc: "JPEG, PNG, HEIC, MP4, MOV supported",
    dropzone: "Select a file or take a photo",
    uploading: "Uploading... ({{progress}}%)",
    uploadSuccess: "Uploaded! It will appear after moderation.",
    moderationNotice: "Your uploads will be reviewed before appearing in the gallery.",
    uploadTips: "Upload Tips",
    tip1: "Capture candid moments from the ceremony",
    tip2: "Short clips of 10–15 seconds work best",
    tip3: "High-quality photos are preferred",
    submitBtn: "Submit to Gallery ({{count}})",
    noFilesSelected: "No files selected yet",
    clearAll: "Clear all",
    addMore: "Add Files",
    filesSelected: "Selected ({{count}})",
    mine: {
      title: "My uploads ({{count}})",
      hint: "You can remove anything you uploaded here — it goes offline at once, the organizer can’t bring it back, and it’s permanently deleted within 7 days.",
      pending: "Awaiting approval",
      approved: "In the gallery",
      delete: "Delete",
      deleteConfirm: "Remove this? It disappears from the gallery and the venue screen right away and the organizer can't bring it back; it is permanently deleted within 7 days.",
      deleted: "Deleted",
      deleteFailed: "Could not delete: {{error}}",
    },
  },
  gallery: {
    title: "Gallery",
    subtitle: "Shared memories",
    empty: "No shared media yet",
    emptyHint: "Be the first to upload a photo!",
    selectedCount: "{{count}} selected",
    swipeHint: "Swipe to navigate",
    prev: "Previous",
    next: "Next",
    close: "Close",
    allMedia: "Approved Media",
    favorites: "Favorites",
    reviewQueue: "Review Queue",
    recycle: "Rejected",
    newAlbum: "New Album",
    albumName: "Album name",
    createAlbum: "Create album",
    deleteAlbum: "Delete album",
    deleteAlbumConfirm: "Are you sure you want to delete this album? Media inside will not be deleted.",
    renameAlbum: "Rename",
    addToAlbum: "Add to album",
    removeFromAlbum: "Remove from album",
    setCover: "Set as cover",
    selectMode: "Select",
    cancelSelect: "Cancel selection",
    selected: "{{count}} selected",
    albumLimitReached: "You can create a maximum of 5 custom albums.",
    onlyApprovedCanBeAdded: "Only approved media can be added to albums.",
    noMedia: "No media yet.",
    noAlbumMedia: "No media in this album yet.",
    loadMore: "Load more",
    itemCount: "{{count}} items",
    addedToFavorites: "Added to favorites",
    removedFromFavorites: "Removed from favorites",
    addedToAlbum: "Added to album",
    removedFromAlbum: "Removed from album",
    albumCreated: "Album created",
    albumDeleted: "Album deleted",
    albumRenamed: "Album renamed",
    coverSet: "Cover photo updated",
    like: "Like",
    unlike: "Unlike",
    liked: "Liked!",
    commentAdded: "Comment added",
    commentDeleted: "Comment deleted",
    deleteComment: "Delete comment",
    noComments: "No comments yet",
    commentPlaceholder: "Add a comment...",
    share: "Share",
    viewAllComments: "View all {{count}} comments",
    likesCount: "{{count}} likes",
    guest: "Guest",
    doubleTapToLike: "Double tap to like",
    uploadPhotos: "Upload",
    searchByName: "Search by guest name...",
    filterNewest: "Newest First",
    filterOldest: "Oldest First",
    download: "Download",
    commentsCount: "{{count}} comments",
    uploadedBy: "Uploaded by {{name}}",
    noResults: "No results found",
    noResultsHint: "Try a different search term",
    albums: "Albums",
    noAlbums: "No albums",
    noPublicAlbums: "No albums shared yet",
    noPublicAlbumsHint: "The organizer hasn't published any albums",
    backToAlbums: "Back to Albums",
    makePublic: "Make Public",
    makePrivate: "Make Private",
    filterAll: "All",
    filterPhotos: "Photos",
    filterVideos: "Videos",
    manageAlbums: "Manage Albums",
    editAlbum: "Edit Album",
    hideComment: "Hide Comment",
    unhideComment: "Show Comment",
    commentHidden: "Hidden",
    commentHiddenSuccess: "Comment hidden",
    commentUnhiddenSuccess: "Comment visible",
    visibility: "Visibility",
    coverPhoto: "Cover Photo",
    public: "Public",
    private: "Private",
    albumSaved: "Album saved",
    addFromGallery: "Add from Gallery",
    addSelected: "Add Selected ({{count}})",
    alreadyInAlbum: "Already in album",
    removeFromAlbumConfirm: "Remove?",
    searchGuest: "Search guests...",
  },

  // ─── Activity Feed ──────────────────────────────
  activity: {
    title: "Activity",
    all: "All",
    reactions: "Reactions",
    uploads: "Uploads",
    likedOne: "liked a photo",
    likedOne_video: "liked a video",
    likedMany: "liked {{count}} photos",
    likedMany_video: "liked {{count}} videos",
    commentedOne: "left a comment",
    commentedMany: "left {{count}} comments",
    uploadedOne: "uploaded a photo",
    uploadedOne_video: "uploaded a video",
    uploadedMany: "uploaded {{count}} photos",
    uploadedMany_video: "uploaded {{count}} videos",
    pendingOne: "1 photo awaiting approval",
    pendingMany: "{{count}} photos awaiting approval",
    newActivity: "{{count}} new activities",
    empty: "No activity yet",
    emptyHint: "Be the first to upload a photo!",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
    you: "You",
    uploaded: "uploaded a photo",
    uploaded_video: "uploaded a video",
    liked: "liked a photo",
    liked_video: "liked a video",
    commented: "commented",
    youUploadedOne: "uploaded a photo",
    youUploadedOne_video: "uploaded a video",
    youUploadedMany: "uploaded {{count}} photos",
    youUploadedMany_video: "uploaded {{count}} videos",
    youLikedOne: "liked a photo",
    youLikedOne_video: "liked a video",
    youLikedMany: "liked {{count}} photos",
    youLikedMany_video: "liked {{count}} videos",
    youCommentedOne: "commented",
    youCommentedMany: "left {{count}} comments",
    unknownGuest: "Guest",
    galleryBanner: "{{count}} new updates ↑",
    galleryBannerTap: "Tap to see",
    toast: {
      newUpload: "{{name}} uploaded a photo 📸",
      newUpload_video: "{{name}} uploaded a video 🎥",
      newLike: "{{name}} liked a photo ❤️",
      newLike_video: "{{name}} liked a video ❤️",
      newComment: "{{name}} commented 💬",
      see: "See →",
    },
  },
  slideshow: {
    title: "Stage",
    live: "Live",
    connecting: "Connecting...",
    waiting: "Waiting for the stage...",
    waitingHint: "Approved photos will appear here",
    sharedBy: "Shared by {{name}}",
    memoriesInSync: "Memories in sync",
    fullscreen: "Fullscreen",
    exit: "Leave",
    exitFullscreen: "Minimize",
    mute: "Mute",
    unmute: "Unmute",
    scanToJoin: "Join the celebration",
    scanToJoinHint: "Scan to share photos & send love",
    tapToReact: "Tap ❤️ from your phone",
    reactedOne: "{{name}} reacted",
    reactedOther: "{{name}} +{{count}} reacted",
    idleHint: "Scan the code to share your first memory",
    watching: "watching",
    presenceTitle: "Watching now",
    presenceMore: "+{{count}} more",
    joined: "{{name}} joined",
    rejiLive: "Reji is live",
    guestFallbackName: "Guest",
  },
  slideshowRemote: {
    playing: "Playing",
    paused: "Paused",
    slideOf: "{{current}} / {{total}}",
    viewers: "{{count}} watching",
    pause: "Pause",
    resume: "Resume",
    next: "Next",
    previous: "Previous",
    controlSuccess: "Command sent",
    controlFailed: "Command failed",
    offline: "Stage is offline",
  },
  reji: {
    title: "Reji",
    subtitle: "Director's Booth",
    live: "LIVE",
    idleNoViewers: "Waiting for viewers — stage is idle",
    connecting: "Connecting...",
    watchMode: "Join the stage",
    exitWatch: "Back to Reji",
    flow: "Flow",
    nowPlaying: "On stage",
    nextUp: "Up next",
    hidden: "Hidden",
    hide: "Hide",
    unhide: "Restore",
    spotlight: "Spotlight",
    gotoSuccess: "Jumped to slide",
    hideSuccess: "Hidden from the show",
    unhideSuccess: "Restored to the show",
    spotlightSuccess: "Spotlighted — playing next",
    orderFirst: "Move First",
    moveNext: "Play next",
    moveEnd: "Move to end",
    nudgeUp: "Move up",
    nudgeDown: "Move down",
    presenceSheetTitle: "Viewers",
    roleGuest: "Guest",
    roleReji: "Reji",
    emptyPlaylist: "No approved media yet",
    openReji: "Open Reji",
    dashboardStatus: "Stage Status",
  },
  admin: {
    watchSahne: "Watch Sahne",
    beVisible: "Be visible",
    watchHidden: "Watching hidden",
    watchOnly: "Watch only",
    presenceName: "Administrator",
    forbidden: "Access Denied",
    forbiddenDesc: "is not registered as a platform administrator.",
    forbiddenHelp: "Contact a platform administrator for access.",
    switchAccount: "Login with Different Account",
    backToWeddings: "Back to All Weddings",
    usersTitle: "Users (Organizers)",
    usersDesc: "Manage platform organizers and account plans",
    weddingsDesc: "Manage and monitor all weddings on the platform",
    packageColumn: "Package",
    packageAccountWide: "account-wide",
    packageTrial: "Trial",
    packageFree: "Free package",
    packageLegacy: "transition",
    packageSuspended: "Suspended (no package)",
    accountPlanShort: "Account: {{plan}}",
    accountPlanTitle: "The organizer's account plan — applies only to weddings without a package; this wedding's package is on the dashboard card",
    usersTotal: "{{count}} organizers total",
    usersEmpty: "No registered users yet.",
    usersLoadFailed: "Failed to load users",
    planUpdateFailed: "Failed to update plan",
    columnPlan: "Plan",
    columnWeddings: "Weddings",
    columnRegistered: "Registered",
    userDetail: "User Detail",
    userWeddings: "User's Weddings",
    noUserWeddings: "This user has no weddings.",
    organizersTitle: "Organizers",
    organizersEmpty: "No organizers assigned to this wedding.",
    organizerRole: "Role",
    organizerAccessHint: "Organizers with access to this wedding",
    addOrganizer: "Add Organizer",
    addFirstOrganizer: "Add first organizer",
    organizerAdded: "Organizer added",
    organizerRemoved: "Organizer removed",
    removeOrganizer: "Remove Organizer",
    removeOrganizerConfirm: "are you sure you want to remove access for this organizer?",
    // ─── Plans management ───────────────────────────
    plans: "Plans",
    addPlan: "New Plan",
    auditLog: "Audit Log",
    planDeleteConfirm: 'Are you sure you want to delete "{{slug}}"?',
    planFieldSlug: "Slug",
    planFieldLabel: "Label",
    planFieldSortOrder: "Order",
    planFieldDefault: "Default",
    planFieldMaxWeddings: "Max Weddings",
    planFieldMaxGuests: "Max Guests",
    planFieldMaxStorage: "Max Storage (GB)",
    planFieldMaxMedia: "Max Media",
    planColLabel: "Label",
    planColSlug: "Slug",
    planColWeddings: "Weddings",
    planColGuests: "Guests",
    planColStorage: "Storage",
    planColMedia: "Media",
    planColUsers: "Users",
    planColStatus: "Status",
    planStatusDefault: "Default",
    planStatusActive: "Active",
    planStatusInactive: "Inactive",
    planHistory: "History",
    planChangeHistory: "Change History",
    planNoHistory: "No change history",
    // ─── Audit log ──────────────────────────────────
    timeJustNow: "just now",
    timeMinutesAgo: "{{count}} min ago",
    timeHoursAgo: "{{count}} hr ago",
    timeDaysAgo: "{{count}} day(s) ago",
    auditDescription: 'All write operations across the platform',
    auditFilterAction: 'Filter action',
    auditSearchAction: 'Search actions…',
    auditRecordCount: '{{count}} records',
    auditTimeJustNow: 'just now',
    auditTimeMinutes: '{{count}}m ago',
    auditTimeHours: '{{count}}h ago',
    auditTimeDays: '{{count}}d ago',
    auditActorAll: "All Actors",
    auditActorAdmin: "Admin",
    auditActorOrganizer: "Organizer",
    auditActorGuest: "Guest",
    auditActorSystem: "System",
    auditActionsAll: "All Actions",
    auditSearchPlaceholder: "Search name, email or ID...",
    auditNoResults: "No records found",
    auditColTime: "Time",
    auditColActor: "Actor",
    auditColIdentity: "Identity",
    auditColAction: "Action",
    auditColResource: "Resource",
    auditColWeddingId: "Wedding ID",
    auditGuestPrefix: "Guest",
    auditLoadMore: "Load More",
    auditMetadata: "Metadata",
    auditMetadataNone: "No metadata available for this record.",
  },
  web: {
    notFound: "Wedding Not Found",
    weddingNotFound: "Wedding Not Found",
    notFoundDesc: "The wedding page you're looking for doesn't exist. Please check your invitation link.",
    invalidSlug: "Please use your invitation link to access.",
    useLinkPrompt: "Please use your invitation link to access.",
    appTitle: "Vite Gallery",
    notifications: "Notifications",
  },

  // ─── QR Self-Registration (Guest) ──────────────────
  join: {
    title: "Wedding Registration",
    subtitle: "Enter your details to join the wedding",
    namePlaceholder: "Full Name",
    nameLabel: "Full Name",
    submit: "Register",
    submitting: "Registering...",
    smsConsentLabel: "I'd like to receive wedding invitation and reminder SMS. (You can opt out any time via the link in the message.)",
    alreadyRegistered: "You're already registered!",
    alreadyRegisteredHint: "This phone number is already linked to this wedding. Tap the button below to sign in.",
    goToLogin: "Log In",
    registrationClosed: "Registration Closed",
    registrationClosedHint: "Guest registration is not currently active for this wedding",
    registrationNotYet: "Registration Not Yet Open",
    registrationOpensWeddingDay: "Registration will be active on the wedding day",
    countdownDays: "days",
    countdownHours: "hrs",
    countdownMinutes: "min",
    countdownSeconds: "sec",
    successOtp: "A verification code has been sent to your phone",
    successAuthed: "Registration complete!",
    capReached: "Registration Temporarily Paused",
    capReachedHint: "Today's registration limit has been reached. Please try again later or contact the couple.",
    guestLimitReached: "Guest limit reached",
    guestLimitReachedHint: "This wedding’s guest limit is full. Please let the couple know — they can extend it. If you’re on the guest list, you can sign in with your phone.",
    foreignNumber: "This number can't be registered.",
    foreignNumberHint: "Verification SMS can only be sent to numbers starting with {{prefixes}}. Please contact the couple so they can add you to the list.",
    invalidName: "Please enter your first and last name — at least 2 letters, no digits or links.",
  },

  // ─── Registration Settings (Organizer) ─────────────
  registration: {
    title: "Registration Settings",
    allowSelfRegister: "Self-registration",
    allowSelfRegisterHint: "Guests can register by scanning the QR code",
    requireOtp: "OTP verification",
    requireOtpHint: "If disabled, entering a phone number is enough — no verification code",
    closedMode: "Permanently closed",
    closedModeHint: "Guests cannot register",
    scheduledMode: "Scheduled opening",
    opensAt: "Registration opens at",
    opensAtHint: "Registration will automatically open at this date and time",
    opensAtRequired: "Please select an opening date and time",
    useWeddingTime: "Use wedding time",
    closesAt: "Registration closes at",
    closesAtHint: "After this time the QR link stops accepting registrations. Leave empty to keep it open.",
    closesAtClear: "Clear",
    closesAtBeforeOpens: "Closing time must be after the opening time",
    closesAtWeekAfterWedding: "1 week after the wedding",
    incentiveTextPlaceholder: "We need your phone number to be able to share the photos with you after the wedding",
    save: "Save",
    saving: "Saving...",
    saved: "Settings saved",
    saveFailed: "Failed to save",
    qrCode: "QR Code",
    qrCodeHint: "Guests scan this QR code to register",
    qrDownload: "Download PNG",
    qrDownloadSvg: "Download SVG",
    qrDownloadPdf: "Download PDF",
    qrCtaShare: "Share Your Photos",
    qrCopyUrl: "Copy URL",
    qrCopied: "Copied!",
    sourceOrganizer: "Added manually",
    sourceQr: "Joined via QR",
    weddingDetails: "Wedding Details",
    weddingDetailsHint: "Control which fields guests can fill in during RSVP",
    showDietField: "Dietary preference",
    showDietFieldHint: "Collect dietary restrictions from guests",
    showNotesField: "Special requests",
    showNotesFieldHint: "Allow guests to add a note",
    maxHeadcount: "Guest count per RSVP",
    maxHeadcountHint: "Maximum number of people per RSVP response",
  },

  // ─── Support Tickets ───────────────────────────────
  support: {
    title: "Support Tickets",
    newTicket: "Open New Ticket",
    subject: "Subject",
    body: "Description",
    status: "Status",

    noTickets: "No support tickets yet.",
    selectTicket: "Select a ticket",
    selectTicketHint: "or open a new support request",
    statusOpen: "Open",
    statusInReview: "In Review",
    statusResolved: "Resolved",
    statusClosed: "Closed",
    submit: "Submit",
    submitting: "Submitting...",
    successCreate: "Your support ticket has been submitted.",
    settingsSaved: "Saved",
    backToList: "Back to Tickets",
    weddingLabel: "Related Wedding",
    generalTicket: "General (not wedding-specific)",
    minCharsSubject: "Subject must be at least 5 characters",
    minCharsBody: "Description must be at least 20 characters",
    replyPlaceholder: "Type your message...",
    sendMessage: "Send",
    sending: "Sending...",
    ticketClosed: "This ticket is closed, no new messages can be sent.",
    you: "You",
    admin: "Support Team",
    unreadDot: "New message",
    priorityLow: "Low",
    priorityNormal: "Normal",
    priorityHigh: "High",
    priorityUrgent: "Urgent",
    categoryGeneral: "General",
    categoryBilling: "Billing",
    categoryTechnical: "Technical",
    categoryBug: "Bug Report",
    categoryFeatureRequest: "Feature Request",
    priority: "Priority",
    category: "Category",
    internalNote: "Internal Note",
    internalNoteHint: "This note is only visible to the admin team, not to the organizer.",
    sendAndResolve: "Send & Resolve",
    reopen: "Reopen",
    reopenConfirm: "This ticket will be reopened. Are you sure?",
    csatTitle: "Rate your support experience",
    csatHint: "This ticket is resolved. You can rate your experience from 1 to 5.",
    csatSubmit: "Submit Rating",
    csatThanks: "Thank you for your feedback!",
    csatAlreadyRated: "You have already rated this ticket.",
    cannedResponses: "Canned Responses",
    cannedResponsesEmpty: "No canned responses yet.",
    addCannedResponse: "Add Canned Response",
    cannedTitle: "Title",
    cannedContent: "Content",
    deleteCannedConfirm: "This canned response will be deleted. Are you sure?",
    insertCanned: "Insert canned response",
    attachments: "Attachments",
    attachmentHint: "Up to 2 photos, max 5 MB each (JPEG, PNG, WebP, GIF)",
    attachmentUploading: "Uploading...",
    attachmentError: "Failed to upload file",
    removeAttachment: "Remove attachment",
    ticketNumber: "Ticket #{{number}}",
    sendNotification: "Send notification",
    noNotification: "No notification",
    monthlyLimitReached: "You have reached the monthly ticket limit (max 10/month). Resolve existing tickets first.",
    csatRated: "Rated",
    csatUnrated: "Awaiting Rating",
    tabActive: "Active",
    tabResolved: "Resolved",
    attachFiles: "Attach files",
    fileTypeError: "Only JPEG, PNG, WebP and GIF files are supported.",
    fileSizeError: "Each file must be at most 5 MB.",
  },
  adminSupport: {
    title: "Support Tickets",
    settings: "Support Settings",
    allTickets: "All Tickets",
    noTickets: "No support tickets.",
    from: "From",
    replyPlaceholder: "Write your reply...",
    saveReply: "Save Reply",
    saving: "Saving...",
    updateStatus: "Update Status",
    settingsTitle: "Support Configuration",
    emailLabel: "Notification Email Address",
    sendMessage: "Send",
    sending: "Sending...",
    unreadDot: "New message",
  },
  sms: {
    title: "SMS Invitations",
    description: "Send invitation and RSVP reminder SMS to your guests",
    quotaLabel: "Sends left",
    quotaExhausted: "You have no sends left",
    smsQuotaLabel: "SMS credits left",
    smsQuotaExhausted: "You have no SMS credits left",
    channelDisabled: "SMS sending has been temporarily disabled by the administrator. Your scheduled campaigns will be sent automatically once the channel is re-enabled.",
    campaignsDisabled: "SMS campaigns haven't been enabled by the administrator yet. Your scheduled campaigns will be sent automatically once enabled.",
    sendDisabledHint: "SMS sending is disabled by the administrator — sending is not possible.",
    newCampaign: "New Campaign",
    nameLabel: "Campaign name",
    namePlaceholder: "e.g. First invitation",
    templateLabel: "Message template",
    defaultTemplate: "Dear {{guest_name}}, we would be delighted to have you with us at the wedding of {{couple_names}} on {{date}}. RSVP here: {{rsvp_link}}",
    previewLabel: "Preview",
    previewEmpty: "Your message will appear here",
    sampleGuest: "Jane Smith",
    segmentInfo: "{{chars}} characters · {{segments}} SMS segment(s)",
    ucs2Hint: "(special characters reduce each segment to 70 characters)",
    segmentLabel: "Who receives it?",
    segments: {
      all: "All guests",
      rsvp_none: "No RSVP yet",
      rsvp_no: "Declined",
      rsvp_maybe: "Undecided",
      manual: "Pick manually",
    },
    recipientCount: "{{count}} recipient(s)",
    searchGuests: "Search guests…",
    selectedCount: "{{count}} selected",
    noGuests: "No guests found",
    scheduleLabel: "When is it sent?",
    schedules: {
      now: "Send now",
      scheduled: "Schedule",
      auto_chain: "Auto reminder",
    },
    scheduledAtLabel: "Send time",
    chainDaysLabel: "Days between",
    chainRoundsLabel: "Max rounds",
    chainHint: "Each round only goes to guests who still haven't RSVP'd; anyone who responds drops out of the chain automatically.",
    testPhonePlaceholder: "+1…",
    sendTest: "Send test",
    testHint: "Goes to your verified number (verify it in Profile).",
    testDisabledAdminHint: "Admins have no verified phone number on file — test sends aren't available here.",
    goToControlCenter: "Go to control center",
    excludedNoConsent: "{{count}} no consent",
    testSent: "Test SMS sent",
    costEstimate: "Estimated cost: ~${{cost}}",
    submitNow: "Send",
    submitSchedule: "Schedule",
    created: "Campaign created",
    canceled: "Campaign canceled",
    delete: "Delete",
    deleteConfirm: "This campaign will be permanently deleted. Are you sure?",
    deleted: "Campaign deleted",
    cancel: "Cancel",
    details: "Details",
    history: "Campaign History",
    noCampaigns: "No campaigns yet. Send your first invitation above.",
    roundsUsed: "{{count}} round(s)",
    nextRound: "next: {{date}}",
    held: "On hold",
    holdBadge: "On hold",
    holdReasons: {
      channel_disabled: "On hold because an administrator temporarily turned SMS sending off. It will go out automatically once the channel is back on.",
      campaigns_disabled: "On hold because SMS campaigns have not been enabled by an administrator yet. It will go out automatically once they are.",
      not_configured: "On hold because SMS sending has not been configured yet — please contact an administrator.",
      spend_breaker: "On hold because a protective spending limit kicked in. It will go out automatically once an administrator resets it.",
      daily_cap: "On hold because the daily sending limit was reached. It will go out automatically once the limit resets.",
    },
    approval: {
      pendingBadge: "Awaiting approval",
      approvedBadge: "Approved",
      rejectedBadge: "Rejected",
      pendingHint: "This list of {{count}} exceeds the threshold, so it is waiting for administrator approval. It will go out automatically once approved.",
      rejectedHint: "An administrator rejected this campaign; nothing will be sent. You can create a new campaign with a smaller segment.",
      rejectedHintWithReason: "Rejected by an administrator: {{reason}}",
      createdPending: "Your campaign was created — the list of {{count}} exceeds the threshold, so it is waiting for administrator approval. It will go out automatically once approved.",
      pageTitle: "SMS approvals",
      pageDescription: "Campaigns over the threshold wait here — not a single SMS goes out until they are approved.",
      empty: "No campaigns awaiting approval",
      emptyHint: "Every campaign whose recipient count exceeds the threshold lands here automatically.",
      thresholdNote: "Current threshold: {{count}} recipients. Change it in the Control Center.",
      recipients: "{{count}} recipient(s)",
      recipientsUnknown: "Recipient count could not be determined (approval requested to stay on the safe side)",
      estimatedCost: "Estimated cost",
      segmentsPerMessage: "{{count}} segment(s) per message",
      wedding: "Wedding",
      organizer: "Organizer",
      createdAt: "Created",
      scheduledFor: "Scheduled send",
      sendNowLabel: "Immediately (awaiting approval)",
      messagePreview: "Message preview",
      approve: "Approve",
      reject: "Reject",
      approving: "Approving…",
      rejecting: "Rejecting…",
      rejectReasonLabel: "Rejection reason (optional)",
      rejectReasonPlaceholder: "e.g. list too large, segment should be narrowed",
      rejectCancel: "Cancel",
      rejectConfirm: "Reject",
      approveSuccess: "Campaign approved — sending has been released.",
      rejectSuccess: "Campaign rejected — nothing will be sent.",
      actionError: "The action could not be completed",
      loadMore: "Load more",
    },
    round: "Round {{n}}",
    noSendsYet: "Nothing sent yet.",
    center: {
      pageTitle: "SMS Center",
      pageDescription: "SMS campaigns across every wedding: approval queue and actual spend.",
      tabPending: "Awaiting Approval",
      tabAll: "All Campaigns",
      filterWedding: "Wedding",
      filterWeddingAll: "All weddings",
      filterStatus: "Status",
      filterStatusAll: "All statuses",
      filterApproval: "Approval",
      filterApprovalAll: "All approval states",
      filterApprovalNone: "Never needed approval",
      filterIncludeDeleted: "Show deleted",
      filterReset: "Clear filters",
      pageTotals: "{{count}} campaigns on this page · {{segments}} segments · ~${{usd}}",
      empty: "No campaigns",
      emptyHint: "No SMS campaign has been created for any wedding yet.",
      emptyFiltered: "No campaign matches the selected filters.",
      deletedBadge: "Deleted",
      spent: "Spent",
      segmentsUsed: "{{count}} segments",
      sends: "Sends",
      deliveredFailed: "{{delivered}} delivered · {{failed}} failed",
      audience: "Audience",
      lastDispatch: "Last dispatch",
      nextDispatch: "Next: {{at}}",
      rejectionReason: "Rejection reason: {{reason}}",
      createdAt: "Created: {{at}}",
      unitPrice: "unit ${{usd}}",
    },
    status: {
      scheduled: "Scheduled",
      active: "Active",
      dispatching: "Sending",
      completed: "Completed",
      canceled: "Canceled",
      failed: "Failed",
    },
    sendStatus: {
      queued: "Queued",
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      undelivered: "Undelivered",
    },
    table: {
      guest: "Guest",
      phone: "Phone",
      round: "Round",
      status: "Status",
    },
  },

  errorBoundary: {
    title: "Something went wrong opening this page",
    desc: "Other pages still work. Reload the page; if it keeps happening, send us the details.",
    reload: "Reload page",
    details: "Technical details",
  },
  controlCenter: {
    loadPartialFailed: "{{count}} data sources failed to load — what's shown may be incomplete.",
    accountsPrivacy: {
      title: "Accounts and privacy",
    },
    title: "Control Center",
    description: "Kill switches, financial limits, SMS spend and delivery logs",
    type: "Type",
    killSwitch: "Kill Switch",
    group: {
      sms: "SMS",
      other: "Other",
    },
    smsEnabled: "SMS Master Switch",
    smsEnabledDesc: "Stops every SMS incl. campaigns + OTP (master).",
    emailEnabled: "Email Master Switch",
    emailEnabledDesc: "All email sending (OTP, alerts, support).",
    smsCampaignsEnabled: "SMS Campaigns",
    smsCampaignsDesc: "Invitation/reminder SMS; doesn't affect login (OTP).",
    masterBadge: "ALL",
    enabled: "Enabled",
    disabled: "Disabled",
    configSaved: "Configuration saved",
    stats: "Statistics",
    sms24h: "SMS (24h)",
    email24h: "Email (24h)",
    failed24h: "Failed (24h)",
    blocked24h: "Blocked (24h)",
    sms7d: "SMS (7d)",
    email7d: "Email (7d)",
    logs: "Send Log",
    recipient: "Recipient",
    status: "Status",
    error: "Error",
    weddingId: "Wedding",
    createdAt: "Date",
    noLogs: "No records found.",
    loadMore: "Load More",
    filters: {
      all: "All",
      sms: "SMS",
      email: "Email",
      sent: "Sent",
      failed: "Failed",
      blocked: "Blocked",
    },
    campaigns: {
      title: "SMS Campaigns",
      active: "Active Campaigns",
      sends24h: "Sends (24h)",
      failed24h: "Failed (24h)",
      sends7d: "Sends (7d)",
      held: "{{count}} round(s) on hold",
      pendingApproval: "{{count}} campaign(s) awaiting approval",
      reviewLink: "Open the approval queue",
    },
    spend: {
      title: "SMS Spend",
      segments: "Segments (today)",
      estCost: "Est. cost",
      breaker: "Circuit Breaker",
      tripped: "Spend breaker tripped ({{reason}}) — campaign sending stopped.",
      reset: "Reset breaker",
      resetDone: "Breaker reset",
    },
  },

  finance: {
    title: "Financial Limits",
    capsTitle: "Caps (limits)",
    capsHint: "Edit the values — only changed fields are saved. The server validates the final bounds.",
    save: "Save",
    override: "Custom",
    default: "Default",
    resetField: "Reset to default",
    resetToDefault: "Reset to default",
    unitMb: "MB",
    unitUsd: "USD",
    rangeHint: "Must be between {{min}}–{{max}}",
    saveSuccess: "Financial caps updated",
    resetSuccess: "Reset to default",
    boundsError: "Value is out of the allowed range",
    switchEnabled: "Enabled",
    switchDisabled: "Disabled",
    caps: {
      verifyDailyCap: "Daily Verify (OTP) cap",
      smsSegmentDailyCap: "Daily SMS segment cap",
      smsPreviewDailyCap: "Daily SMS preview cap",
      smsRoundRecipientHardCap: "Per-round recipient cap",
      smsManualSegmentMaxGuests: "Manual segment max guests",
      emailDailyCap: "Daily email cap",
      smsTrPriceUsd: "SMS unit price (TR)",
      maxImageBytes: "Max photo size",
      maxVideoDirectBytes: "Max video size (direct)",
      qrScanVisitorDailyCap: "Daily QR scans per visitor",
      selfRegisterDailyCap: "Daily self-registrations per wedding",
      smsApprovalThreshold: "SMS approval threshold (recipients)",
    },
    capsDesc: {
      selfRegisterDailyCap: "Maximum registrations a wedding accepts from its QR/join link in 24 hours. The link is public, so if it leaks or a bot gets through, the guest list and OTP SMS budget stop here; beyond the cap new registrations are rejected and an alert is sent to the admin channel. Guests added manually by the organizer don't count.",
      smsApprovalThreshold: "If a campaign's recipient count EXCEEDS this value, the campaign is created but not a single SMS goes out until an administrator approves it. 0 = every campaign needs approval (lockdown mode), 5000 = effectively disabled. Campaigns below the threshold go out automatically as before.",
      qrScanVisitorDailyCap: "How many QR scan rows one device may write per day. The identity is a hash of wedding + IP + browser (user-agent) + day — not IP alone, so every device on the venue's shared WiFi gets its own quota. Scans over the cap are silently not stored (the guest is still redirected).",
    },
    switchesTitle: "Kill Switches",
    switchesHint: "All ON by default. Turning off dangerous ones requires confirmation.",
    switches: {
      verifyEnabled: "SMS OTP Login",
      uploadsEnabled: "Media Uploads",
      imageTransformEnabled: "CDN Image Resize",
      smsApprovalEnabled: "SMS Approval Gate",
      organizerSeesGuestDeletedMedia: "Organizer sees media removed by guests",
    },
    switchesDesc: {
      smsApprovalEnabled: "⚠️ UNLIKE the others: ON = the gate is active (campaigns over the threshold wait for approval). Turning it off removes the approval requirement entirely — campaigns over the threshold go out directly.",
      verifyEnabled: "Guest/organizer SMS login codes; doesn't affect campaigns.",
      uploadsEnabled: "Guest photo/video uploads; turning off stops all uploads.",
      imageTransformEnabled: "On-the-fly image resizing. Safe — doesn't affect uploads.",
      organizerSeesGuestDeletedMedia: "On: media a guest removed themselves appears read-only in the organizer's “Deleted” list (can't be restored). Off (default): only the platform administrator sees it.",
    },
    monitoringTitle: "Live Monitoring",
    mon: {
      verify: "Verify (today)",
      segments: "SMS segments (today)",
      breaker: "Spend breaker",
      breakerOk: "Normal",
      breakerTripped: "Tripped",
    },
    confirm: {
      title: "Dangerous action",
      verifyWarning: "If Verify is turned off, guests/organizers cannot sign in via SMS.",
      uploadsWarning: "If uploads are turned off, guests cannot upload photos.",
      detail: "You can turn this setting back on at any time.",
      turnOff: "Turn off",
      approvalWarning: "If the approval gate is turned off, SMS campaigns above the threshold are sent without approval.",
    },
  },

  maintenance: {
    title: "Error Tracking",
    overview: "Overview",
    totalGroups: "Total Error Groups",
    unresolved: "Unresolved",
    last24h: "Last 24 Hours",
    last7d: "Last 7 Days",
    topRoutes: "Most Frequent Routes",
    errorGroups: "Error Groups",
    events: "Events",
    markResolved: "Mark Resolved",
    markUnresolved: "Reopen",
    resolutionNote: "Resolution note",
    resolutionSaved: "Saved",
    firstSeen: "First Seen",
    lastSeen: "Last Seen",
    totalCount: "Total",
    requestId: "Request ID",
    purge: "Purge Old Records",
    purgeConfirm: "All records older than 30 days will be deleted. Continue?",
    purgeDetail: "Deletes records older than 30 days from error_log + error_groups. A cron job runs this automatically every night at 03:00.",
    purged: "Old records purged",
    stackTrace: "Stack Trace",
    noErrors: "No errors found.",
    groupNotFound: "Error group not found.",
    description: "Error groups and system health monitoring",
    statusResolved: "Resolved",
    statusOpen: "Open",
    colLastSeen: "Last seen",
    colKind: "Kind",
    colMethod: "Method",
    colPath: "Route",
    colError: "Error",
    colCount: "Count",
    message: "Message",
    dateLabel: "Date",
    weddingLabel: "Wedding",
    actorLabel: "Actor",
    timeJustNow: "just now",
    timeMinutes: "{{count}}m",
    timeHours: "{{count}}h",
    timeDays: "{{count}}d",
    kind: {
      exception: "Exception",
      soft: "Soft Error",
    },
    filters: {
      all: "All",
      unresolvedOnly: "Open only",
      pathSearch: "Search route… (Enter)",
    },
  },
  // ─── Home Feed (Guest) ─────────────────────────────
  home: {
    rsvpNudge: "Are you attending the wedding?",
    rsvpNudgeAction: "Reply",
    rsvpNudgeTitle: "We're waiting for you!",
    rsvpNudgeText: "Please RSVP to help us with the planning.",
    rsvpNudgeRespond: "Respond Now",
    newActivity: "{{count}} new moments shared",
    topLiked: "Most Loved",
    recentActivity: "Recent Activity",
    seeAll: "See all",
    viewGallery: "View Gallery",
    welcomeBack: "Welcome back, {{name}}",
    rsvpStatus: "Your RSVP Status",
    rsvpNotResponded: "You haven't responded yet",
    partyOf: "Party of {{count}}",
    weddingDetails: "Wedding Details",
    dateTime: "Date & Time",
    venue: "Venue",
    updateRsvp: "Update RSVP",
    respondNow: "Respond Now",
    noMediaYet: "No photos shared yet",
    noMediaYetHint: "Be the first to upload a photo!",
    noActivityYet: "No activity yet",
    empty: "No activity yet. Be the first to upload a photo!",
    countdown: {
      daysLeft: "days until the wedding",
      daysAgo: "days since the wedding",
      dayUnit: "Days",
      hoursUnit: "Hrs",
      minsUnit: "Mins",
      secsUnit: "Secs",
      label: "Countdown to I Do",
      labelFor: "{{event}} — Countdown",
      todayFor: "Today is the {{event}}!",
      today: "Today is the wedding day!",
    },
  },

  themes: {
    title: "Themes",
    description: "Default backgrounds used platform-wide. Organizers can override them per wedding.",
    backgrounds: "Backgrounds",
    slotGuestAuth: "Guest Login",
    slotOrganizerAuth: "Organizer Login",
    slotGuestCountdown: "Countdown",
    slotGuestRsvp: "RSVP",
    slotGuestAuthDesc: "Background shown on the guest app login screen",
    slotOrganizerAuthDesc: "Background shown on the organizer panel login screen",
    slotGuestCountdownDesc: "Background shown on the countdown page before the wedding",
    slotGuestRsvpDesc: "Background shown on the guest RSVP page",
    upload: "Upload",
    change: "Change",
    deleteSlot: "Delete",
    uploaded: "Uploaded",
    deleted: "Deleted",
    uploadFailed: "Upload failed",
    deleteFailed: "Delete failed",
    noBackground: "No background",
    dragDrop: "Click or drag to upload an image",
    fileHint: "Max 10MB · JPEG, PNG, WebP, AVIF · Recommended 1920×1080",
    active: "Active",
    confirmDelete: "Are you sure you want to delete this background?",
    // ─── Join Themes (Organizer) ──────────────────────
    presets: "Preset Themes",
    customize: "Customize",
    palette: "Color Palette",
    fontPair: "Font Pair",
    images: "Images",
    heroImage: "Hero Image",
    heroImageHint: "Invitation page background",
    gallery: "Gallery",
    galleryHint: "Max 5 photos",
    preview: "Preview",
    publish: "Publish",
    customized: "Customized",
    reset: "Reset",
    saveSuccess: "Theme saved",
    saveError: "Save failed",
    uploadError: "Upload failed",
    deleteError: "Delete failed",
    fontPairHeadingPreview: "Wedding Title",
    fontPairBodyPreview: "Guest invitation text preview",
    heroImageDesc: "The full-screen background image guests see when they first open the invitation link. Falls back to a theme gradient if not set.",
    galleryDesc: "Photos displayed in the gallery section of the invitation page. Leave empty to hide the gallery section.",
    joinTitle: "Invitation Page Theme",
    joinSubtitle: "Customize the look of the page guests see when they receive your wedding invitation.",
    previewCoupleName: "Emma & James",
    previewDateLocation: "September 12, 2026 · Paris",
    previewJoinTitle: "Join Us",
    previewJoinSubtitle: "Just your name and phone number to accept the invitation.",
    previewRegisterBtn: "Register",
    uploadTooLarge: "File too large. Maximum 10 MB per photo.",
    uploadInvalidType: "Only JPEG, PNG, WebP or AVIF photos are allowed.",
    videoSection: "Scroll Animation",
    videoDesc: "As guests scroll down, the video advances frame by frame (Apple product page effect). Upload MP4 or WebM.",
    videoUpload: "Upload Video",
    videoExtracting: "Extracting frames...",
    videoUploading: "Uploading...",
    videoProgress: "{{done}} / {{total}} frames",
    videoFrameCount: "{{count}} frames ready",
    videoRemove: "Remove Animation",
    videoSuccess: "Scroll animation ready",
    videoError: "Error processing video",
    videoInvalidType: "Only MP4 or WebM video files are allowed.",
    videoTooLarge: "Video too large. Maximum 200 MB.",
    deleteImageConfirm: "Are you sure you want to delete this image?",
    deleteVideoConfirm: "Are you sure you want to remove the scroll animation? All frames will be deleted.",
    mobile: "Mobile",
    desktop: "Desktop",
    noHero: "No image",
    addImage: "Add image",
    changeImage: "Change",
    layoutPickerTitle: "Layout Theme",
    layoutPickerSubtitle: "Pick the classic layout or one of the premium themes. Editable themes let you fully customize every text and image.",
    layoutEditorTitle: "Theme settings",
    layoutEditorSubtitle: "Customize text, images and colors.",
    // ─── Layout picker group/status + theme names ──────
    layoutGroupClassic: "Classic",
    layoutGroupPremium: "Premium",
    layoutStatusEditable: "Editable",
    layoutStatusClassic: "Classic theme",
    layoutStatusPreview: "Preview",
    layoutNames: {
      default: "Classic (default)",
      cinematic: "Cinematic",
      minimal: "Minimal",
      parallax: "Romantic Parallax",
      editorial: "Bold Editorial",
      "dark-romance": "Dark Romance",
    },
  },

  // ─── Landing (marketing site, `/`) ─────────────────
  // ─── Baskı (ortak paket @wedding/print-kit) ─────────
  printKit: {
    title: "Print",
    description: "QR cards made for your wedding — with the couple’s names, date and your own QR code. Use “Print / PDF” to get a true-size PDF for the print shop.",
    nameA: "First name",
    nameB: "Second name",
    date: "Date",
    tables: "Table numbers",
    tablesHint: "E.g. 1-20, VIP — one page per table (up to {{max}}).",
    tableCard: "Table card",
    tableCardSize: "A6 · 105 × 148 mm",
    welcomeSign: "Welcome sign",
    welcomeSignSize: "50 × 70 cm",
    handCard: "Hand card",
    handCardSize: "85 × 55 mm",
    pages: "{{count}} pages",
    print: "Print / PDF",
    printing: "Print dialog opened — choose “Save as PDF” as the destination.",
    printFailed: "Could not open the print dialog.",
    qrTarget: "The QR opens:",
    langNote: "card text in the wedding language ({{lang}})",
    tip: "Tip: scan the QR on a test print with your phone before sending to the printer. Keep “Background graphics” on in the browser.",
    tpl: {
      shareEyebrow: "Share your moments with us",
      step1: "Scan",
      step2: "Share",
      step3: "Watch on screen",
      table: "Table",
      welcome: "Welcome",
      headlineA: "Every moment tonight",
      headlineB: "is in your hands.",
      captionStrong: "Scan with your camera,",
      caption: "share your photos and videos — they appear on the big screen within seconds.",
      noApp: "No app needed",
      handEyebrow: "Tonight",
      handLine1: "one of the moments",
      handLine2: "is yours.",
      handSteps: "Scan · Share · Watch",
    },
  },

  // ─── Canlı demo girişi (web, F7) ─────────
  demoEnter: {
    title: "Welcome to the live demo",
    body: "Look around as a guest: upload photos, leave hearts, open Sahne. No phone number needed.",
    nameLabel: "Your name (optional)",
    namePlaceholder: "E.g. Deniz",
    submit: "Join the demo",
    entering: "Joining…",
    note: "What you upload goes to moderation, is never public and is deleted every night.",
    failed: "Couldn’t join the demo right now — try again shortly.",
    capReached: "The demo is very busy today — try again tomorrow.",
  },

  // ─── Albümü indir (ortak paket @wedding/album-export) ─────────
  albumExport: {
    button: "Download album",
    meta: "{{count}} items · {{size}}",
    empty: "No approved photos yet.",
    onlyApproved: "Approved photos only, original quality (ZIP)",
    partsHint: "The album is large, so it is split into {{parts}} parts; download each one.",
    part: "Part {{n}} ({{from}}–{{to}})",
    started: "Download started — large albums can take a few minutes.",
    failed: "Could not load album info.",
  },

  // ─── Admin: Satış & Site (landing içeriği) ─────────
  siteAdmin: {
    title: "Sales & Site",
    description: "Manage the prices and content shown on the marketing site (wedding.mertbal.com). Saved changes appear on the site within 1–2 minutes.",
    openSite: "Open site",
    reload: "Reload",
    tabs: {
      pricing: "Prices",
      leads: "Quote requests",
      proof: "Social proof",
      media: "Showcase",
      retention: "Retention",
      demo: "Guest demo",
      sales: "Online sales",
      legal: "Legal",
      ledger: "Ledger",
    },
    save: "Save",
    saving: "Saving…",
    saved: "Saved — live on the site within 1–2 minutes.",
    saveFailed: "Could not save: {{error}}",
    invalid: "Some fields are invalid — fix the highlighted rows.",
    loadFailed: "Could not load settings.",
    retry: "Retry",
    unsaved: "Unsaved changes",
    pricing: {
      showTitle: "Show prices on the site",
      showDesc: "When off, package cards show no prices, online checkout closes and the form shows no estimate; visitors ask for a quote.",
      packagesTitle: "Package prices",
      packagesHint: "Price and guest/media/storage numbers come from the package definition — the site card, checkout, quote form and manual sales all use the same numbers. No pricing by guest range.",
      editPackages: "Edit in Packages",
      packageGuests: "up to {{n}} guests",
      noteTitle: "Price note",
      noteHint: "Shown under the package cards. When empty, the default text is shown in 4 languages; a note written here is not translated.",
    },
    leads: {
      unavailableTitle: "Request storage is not enabled yet",
      unavailableBody: "Once the database update (migration 052) is applied, requests from the form are listed here. Until then they arrive by email only.",
      funnelTitle: "Quote form — last 30 days",
      funnelHint: "Anonymous daily counters; each step counts once per page load. Percentages are relative to visitors who saw the form.",
      stepView: "Saw the form",
      stepStep1: "Started filling",
      stepStep2: "Reached contact step",
      stepSubmit: "Submitted",
      pct: "{{pct}}%",
      filterAll: "All",
      planChosen: "{{plan}} package",
      planCustom: "Bigger · custom quote",
      statusLabel: "Status",
      status: {
        new: "New",
        contacted: "Contacted",
        won: "Won",
        lost: "Lost",
      },
      empty: "No requests for this filter.",
      emailNotSent: "Email not sent",
      noEstimate: "No estimate",
      note: "Note",
      notePh: "Call notes (admins only)",
      saveNote: "Save note",
      delete: "Delete",
      deleteConfirm: "Permanently delete the request from {{name}}? (for a data-deletion request)",
      deleted: "Request deleted.",
      updated: "Updated.",
      failed: "Action failed: {{error}}",
      loadMore: "Load more",
      loadFailed: "Could not load requests.",
    },
    proof: {
      statsTitle: "Real usage numbers",
      statsDesc: "Shown on the site as “X wedding albums · Y photos and videos shared”. Numbers come from the database; a number below its threshold is never shown.",
      show: "Show numbers on the site",
      minWeddings: "Display threshold — weddings",
      minMedia: "Display threshold — photos",
      current: "Now",
      currentWeddings: "{{n}} weddings (with at least {{min}} approved photos)",
      currentMedia: "{{n}} approved photos",
      visible: "shown on the site",
      hidden: "below threshold, hidden",
      off: "off",
      unknown: "Numbers could not be read right now.",
      testimonialsTitle: "Couple testimonials",
      testimonialsDesc: "Only add real testimonials you have permission to publish — fabricated reviews are unlawful advertising. Text is shown in the language entered.",
      empty: "No testimonials yet — the section is hidden on the site.",
      add: "Add testimonial",
      couple: "Couple",
      couplePh: "Ayşe & Can",
      when: "When",
      whenPh: "June 2026",
      city: "City",
      cityPh: "Izmir",
      quote: "Testimonial",
      quotePh: "Every photo our guests took was in the album the next morning…",
      rating: "Rating",
      noRating: "No rating",
      showOnSite: "Show on site",
      moveUp: "Move up",
      moveDown: "Move down",
      remove: "Remove",
      limit: "Up to {{max}} testimonials.",
    },
    media: {
      consent: "Only upload photos and videos the couple has agreed to publish; content showing guests’ faces needs their consent.",
      photosTitle: "Real wedding photos",
      photosHint: "Once uploaded they replace the stock images on the site, used in this order across every photo slot. Landscape, at least 1600 px; JPEG/PNG/WebP/AVIF, up to 10 MB.",
      photosEmpty: "No photos yet — the site shows stock images.",
      addPhotos: "Add photos",
      videoTitle: "Sahne video",
      videoHint: "Shown as a player under the Sahne section, titled “From a real wedding”. MP4 (H.264) only, up to 95 MB; 30–60 s recommended. A cover image keeps the page from downloading the video on load.",
      video: "Video",
      videoEmpty: "No video — the block is hidden.",
      poster: "Cover image",
      posterEmpty: "No cover — the browser shows the video’s first frame.",
      upload: "Upload",
      replace: "Replace",
      uploadingN: "Uploading {{n}}/{{total}}",
      processing: "Uploaded, saving on the server…",
      uploaded: "{{count}} file(s) uploaded.",
      removeConfirm: "Permanently remove this file from the site and storage?",
      removed: "Removed.",
      err: {
        too_large: "File too large (images 10 MB, video 95 MB).",
        mp4_only: "Video must be MP4.",
        image_only: "The file is not a valid JPEG/PNG/WebP/AVIF image.",
        limit: "Up to 12 photos.",
        network: "Network error — try again.",
        media_changed: "The showcase changed in another tab — the list was reloaded, please try again.",
        length_required: "Could not read the file size — please try again.",
      },
    },
    retention: {
      title: "Album retention promise",
      desc: "Shown in the site FAQ and the plan note. The system never auto-deletes approved photos; this is the minimum period promised to customers.",
      show: "Show the promise on the site",
      months: "Minimum period (months)",
      hidden: "Promise hidden — the retention question is not shown in the FAQ.",
      warn: "Shortening the period does not change what was promised to customers who bought under it.",
    },
    demo: {
      title: "Guest demo (sample wedding)",
      desc: "Visitors enter /sample-wedding without a phone number and try the guest app (upload, gallery, Sahne). This is not an invitation sample. Their uploads go to moderation, are never public and are deleted every night.",
      state: "Status",
      live: "Live",
      none: "Not set up",
      seedMedia: "Seed photos",
      visitors: "Today’s visitors",
      pending: "Awaiting approval",
      setup: "Set up demo",
      reset: "Reset",
      resetConfirm: "Delete everything in the demo wedding and set it up again? (visitors, uploads, comments)",
      setupDone: "Demo ready — {{count}} seed photos.",
      clearVisitors: "Clear visitors",
      clearConfirm: "Delete today’s demo visitors and their uploads? Seed photos stay.",
      cleared: "{{count}} visitor(s) cleared.",
      open: "Open guest demo",
      moderation: "Go to moderation",
      seedHint: "Seed photos are copied from the real photos in Showcase; if it is empty, stock images are used. The nightly cleanup runs in production only.",
      slugTaken: "“{{slug}}” belongs to a real wedding — the demo can’t be set up and nothing was deleted.",
      ctaTitle: "Show on the marketing site",
      ctaDesc: "When published, a small “Try it as a guest” link appears in the “Share” step of “How it works”; the main buttons don't change. It can't be published before the demo is set up.",
      onSite: "Published on the site",
      offSite: "Not on the site",
      publish: "Publish on the site",
      unpublish: "Unpublish",
      published: "Guest demo published on the site.",
      unpublished: "Guest demo unpublished.",
    },
    sales: {
      selfServeTitle: "Organizers can open their own account",
      selfServeOn: "On: the organizer panel shows a “Create account” page (/signup) — name + terms consent; a Free account opens once the email code is verified. The sign-in screen still never creates accounts.",
      selfServeOff: "Off (recommended): only existing organizers can sign in; you open new accounts. If an unknown email is entered at sign-in, no account is created and the address gets a “get a quote” notice.",
      closeConfirm: "Close new organizer signups? Existing accounts are not affected.",
      ordersTitle: "Online sales (Buy now)",
      ordersLive: "On and live: “Buy now” shows on paid plan cards; orders appear below.",
      ordersBlocked: "On but NOT live — it appears once the items below are fixed.",
      ordersOff: "Off: visitors only see the quote form.",
      ordersOpenConfirm: "Turn on online sales? “Buy now” will appear on the landing page; payment is by bank transfer and you confirm orders here. Having a lawyer review the contract texts is recommended.",
      ledgerRequired: "Online sales can’t be opened before the ledger (migration 057) is applied — contract and price evidence must be kept.",
      needLegal: "Seller identity (name, address, email) is incomplete:",
      needPricing: "Prices are hidden on the site:",
      ordersHint: "There is no payment gateway (iyzico/PayTR) yet: reply to the order with payment details and mark it “Paid” once the money arrives — the account opens and the buyer gets sign-in + contract emails.",
      selfServeMoved: "Letting organizers open their own account is a platform setting; it now lives in:",
    },
    orders: {
      title: "Orders",
      desc: "Online purchases. Never deleted — kept for 10 years as invoice records.",
      filter: {
        pending: "Pending",
        paid: "Paid",
        cancelled: "Cancelled",
        all: "All",
      },
      unavailable: "The orders table doesn't exist yet — orders appear here once migration 053 is applied (until then purchases return 503).",
      empty: "No orders with this status.",
      more: "Load more",
      loadFailed: "Could not load orders: {{error}}",
      buyer: "Buyer",
      eventDate: "Event",
      address: "Billing",
      email: "Email",
      phone: "Phone",
      created: "Ordered: {{at}}",
      paidAt: "Paid: {{at}}",
      paymentNote: "Payment note",
      paymentNotePh: "Payment note (e.g. transfer 13.10, receipt no.)",
      adminNote: "Internal note",
      adminNotePh: "Internal note (buyer can't see)",
      markPaid: "Mark as paid",
      cancel: "Cancel",
      saveNotes: "Save notes",
      cancelConfirm: "Cancel {{number}}?",
      paidCreated: "Paid — organizer account opened.",
      paidNoEmail: "Paid, but the buyer email could not be sent — try again with “Send email”.",
      deactivated: "This organizer account is deactivated — re-enable it under Users.",
      cancelled: "Order cancelled.",
      cancelPaidConfirm: "Cancel {{number}} (refund)? The package is removed from its wedding: without another package the wedding is suspended (no new guests or uploads). Guests and photos are not deleted.",
      paidExisting: "Paid — linked to the existing account (account plan unchanged).",
      paidEmailSkipped: "No email sent to the buyer (your choice) — deliver the contract copy another way.",
      emailInfoTitle: "Purchase emails",
      emailInfo1: "No email goes to the buyer when the order is placed; the business inbox is notified — reply to that email with the payment details.",
      emailInfo2: "When marked “Paid” (optional), the buyer gets the sign-in link, the wedding the package was applied to, and a copy of the pre-contract information + distance sales contract. It can also be sent later with “Send email”.",
      emailInfo3: "No email on cancellation; you handle the refund correspondence.",
      paidTitle: "Mark as paid",
      applyTo: "Wedding to apply the package to",
      applyNext: "Let the organizer choose (package waits in their account)",
      applyHint: "The package is for a single event; the account plan doesn’t change. The selected wedding’s limits rise to this order’s entitlement.",
      sendEmail: "Send the buyer the sign-in + contract email",
      noEmailWarn: "A copy of the distance sales contract must be delivered to the buyer in durable form — if you don’t send it, deliver it another way.",
      confirmPaid: "Confirm",
      close: "Cancel",
      weddingsFailed: "Couldn’t load weddings: {{error}}",
      entitlement: "Entitlement: {{guests}} guests · {{media}} media · {{storage}}",
      appliedTo: "Wedding: {{title}}",
      appliedNext: "Wedding: waiting for the organizer to choose (package in their account)",
      move: "Move",
      apply: "Apply",
      moved: "Package applied to the wedding.",
      emailSentAt: "Email sent ({{at}})",
      emailNotSent: "Email not sent",
      sendEmailNow: "Send email",
      resendEmail: "Resend",
      emailSent: "Email sent to the buyer.",
      conflict: "This order was processed in another tab — list refreshed.",
      migrationRequired: "“Paid” needs migration 055 (entitlement columns missing).",
      weddingNotOwned: "The selected wedding doesn’t belong to this buyer.",
      manualNew: "Manual order",
      manualHint: "A sale made by quote/bank transfer. The account plan does NOT change — the order becomes an entitlement for a single wedding (when marked “Paid”). Pending orders are cancelled automatically if unpaid within 7 days.",
      legacyBadge: "Transition (old account plan)",
      manualPlan: "Package",
      manualAmount: "Amount (₺)",
      manualAmountEach: "Amount — per package (₺)",
      manualQuantity: "Quantity (packages)",
      manualQuantityHint: "{{count}} separate orders will be opened — each is one wedding's entitlement with its own order number. The organizer assigns each package to a wedding.",
      pickOrganizer: "Existing organizer (optional)",
      pickOrganizerPh: "Search by email or name",
      pickNone: "No matching organizer — enter the buyer details manually.",
      pickFailed: "Search failed, try again.",
      pickedLabel: "Organizer: {{name}} · {{email}}",
      pickChange: "Change",
      pickWeddings: "{{count}} weddings",
      pickHint: "Picking fills name, email and phone from the account; once paid, the package appears in that account's My packages.",
      manualCreate: "Create order",
      manualCreated: "{{number}} created — mark it “Paid” once payment arrives.",
      manualCreatedMany: "{{count}} orders opened: {{numbers}} — mark each “Paid” once payment arrives.",
      fix: "Correct",
      saveFix: "Save correction",
      corrected: "Buyer details corrected.",
      foreignLabel: "Wedding on another account (address)",
      foreignPh: "sample-wedding",
      foreignHint: "If the wedding was created with another email, enter its address (slug); the entitlement goes to that wedding and the buyer’s account is unchanged.",
      guestsShort: "{{n}} guests",
      archived: "archived",
      overCap: "This wedding already has {{guests}} guests; the order allows {{cap}}. Extra guests aren’t deleted, but new QR sign-ups close.",
      impact: "Impact — {{wedding}}: now {{guests}} guests / {{media}} media. Limit after cancelling: {{guestsAfter}} guests / {{mediaAfter}} media.",
      impactOver: "⚠️ After cancelling, the wedding stays OVER its limit: new guests and uploads stop.",
      weddingNotFound: "No wedding at this address.",
      notPending: "Buyer details can only be corrected on a pending order (and separately from a status change).",
      contractCopy: "Contract copy",
      noContract: "No stored contract text for this order (quote sale or order placed before the ledger).",
      ledgerEvents: "{{count}} ledger entries.",
      listPrice: "(list {{price}})",
      refundReasonPrompt: "Refund reason (required, written to the ledger):",
      moveReason: "Reason for moving (required)",
      moveReasonPh: "e.g. it was linked to the wrong wedding",
      paymentRef: "Payment reference (required)",
      paymentRefHint: "Receipt no / transfer description — proof of payment, written to the ledger.",
      packageOption: "{{label}} · {{guests}} guests · {{price}}",
      noPackages: "No sellable package definitions — define one in Packages first.",
      noListPrice: "This package has no list price (sold by quote) — a reason is required.",
      listPriceNote: "List price {{price}} — amount and all numbers come from the package definition.",
      discountNote: "List price {{price}} · discount {{discount}} — a reason is required.",
      reasonRequired: "Reason (required — discount / quote)",
      errors: {
        paymentRef: "A payment reference (receipt / transfer description) is required to mark as paid.",
        reason: "A reason is required (at least 3 characters).",
        packageInUse: "This package has been used (guests/media exist or the event is over) — it can’t be released; refund or move it to another wedding.",
        targetHasPackage: "The selected wedding already has a paid package — one package per wedding.",
        packageNotFound: "Package definition not found or inactive.",
        packageNotSellable: "The default (Free) package can’t be sold.",
        invalidTransition: "This action isn’t possible in the order’s current state.",
      },
    },
    ledger: {
      title: "Sales ledger",
      desc: "Record of price and terms settings, package limits, orders and add-on packs. Append-only: entries can't be changed or deleted and are kept for at least 10 years. Use it to show which price applied on which date and the exact contract text the buyer accepted.",
      verify: "Verify integrity",
      verifyOk: "Ledger intact — {{count}} entries verified end to end.",
      verifyBroken: "Warning: entry #{{seq}} was altered or the chain is broken. Compare with a backup.",
      unavailable: "The ledger doesn’t exist yet — it starts recording once migration 057 is applied (online sales can’t be opened before that).",
      empty: "No entries for this filter.",
      loadFailed: "Couldn’t read the ledger: {{error}}",
      more: "Load more",
      actor: "By",
      contract: "Text accepted by the buyer (SHA-256 digest: {{hash}})",
      filter: {
        all: "All",
        settings: "Prices & terms",
        plan: "Package limits",
        order: "Orders",
        addon: "Add-on packs",
      },
      accountPlanSubject: "Account {{id}}: {{before}} → {{after}}",
      packageSubject: "{{kind}} {{label}} · {{id}}",
      packageFree: "Free package",
      packageLegacy: "Transition package",
      events: {
        created: "created",
        paid: "paid",
        cancelled: "cancelled",
        moved: "moved",
        corrected: "corrected",
        applied: "assigned to wedding",
        updated: "changed",
        deleted: "removed",
        baseline: "value before the ledger",
        accountPlan: "Account plan assigned",
        granted: "package granted",
        assigned: "assigned to wedding",
        replaced: "replaced by a paid package",
        revoked: "revoked",
        noted: "note changed",
        propagated: "definition applied to sold packages",
      },
    },
    addons: {
      save: "Save packs",
      title: "Extra guest packs (extra QR sign-ups)",
      desc: "When a wedding’s guest limit is full, the organizer opens a pack for extra people signing up by QR. A pack is active IMMEDIATELY and shows as “payment pending”; collect it under Online sales → Extra packs. Prices are always read from here (server).",
      enabled: "Organizers can open new packs",
      seats: "Guests",
      price: "Price (₺)",
      seatsFor: "Pack {{n}} guest count",
      priceFor: "Pack {{n}} price",
      perGuest: "≈ {{price}} per guest",
      invalid: "Guests must be 1–1000 and price 0–100,000 whole numbers; no duplicate guest counts.",
      panelTitle: "Extra packs",
      panelDesc: "Extra QR sign-up packs opened by organizers. Active once opened; mark “Paid” when the transfer arrives, or “Cancel” if unpaid (extra seats close at once).",
      filter: { pending: "Payment pending", paid: "Paid", cancelled: "Cancelled", all: "All" },
      markPaid: "Paid",
      cancel: "Cancel",
      paidConfirm: "Mark the +{{seats}} pack for /{{slug}} as paid?",
      paidRefPrompt: "Mark the +{{seats}} pack for /{{slug}} ({{amount}}) as paid? Payment reference (receipt no / transfer description, required):",
      paymentRefRequired: "A payment reference (receipt / transfer description) is required.",
      cancelConfirm: "Cancel the +{{seats}} pack for /{{slug}}? Extra seats close at once; registered guests are not removed.",
      markedPaid: "Pack marked as paid.",
      cancelled: "Pack cancelled.",
      empty: "No packs in this state.",
      unavailable: "The extra pack table doesn’t exist yet — it appears here once migration 056 is applied.",
      loadFailed: "Couldn’t load extra packs: {{error}}",
    },
    legal: {
      title: "Legal identity",
      desc: "The data controller in the KVKK privacy notice and the provider in the Terms of use come from these fields. Name/company, address and email are required before paid sales (orders) can open.",
      publicWarn: "Every field you fill is published PUBLICLY on /privacy and /terms. Leave a field empty if it shouldn't be published — empty fields are not shown at all.",
      complete: "Identity complete — contract and sales pages can be opened.",
      incomplete: "Incomplete: until name/company, address and email are filled, the pages show only the contact email and paid sales stay closed.",
      name: "Full name / company name",
      titleField: "Title / profession (optional)",
      address: "Address",
      phone: "Phone",
      email: "Email (contact@mertbal.com if empty)",
      taxOffice: "Tax office",
      taxId: "Tax / national ID no.",
      mersis: "MERSIS no.",
      kep: "Registered e-mail (KEP)",
      openKvkk: "Open privacy page",
      openTerms: "Open terms page",
      clearConfirm: "Empty and save all legal identity fields? The pages will show only the contact email.",
      clear: "Clear all",
    },
  },

  plansAdmin: {
    siteNote: "The package definition is the SINGLE SOURCE: the site card, online checkout, manual sales and “Grant package” take guest/media/storage/SMS numbers and the PRICE from here. The marketing site shows only Free · Pro · Max; names are fixed. Other packages are private (corporate): hidden on the site, sold by quote / “Grant package”. A sold package is a contract: changing the definition only affects NEW sales; sold packages keep the numbers they were bought with.",
    onSite: "On site",
    onSiteHint: "Shown on the marketing site — can’t be deleted, limits are editable",
    private: "Private",
    privateHint: "Private plan — not shown on the marketing site",
    reactivate: "Reactivate",
    created: "Plan created.",
    updated: "Plan updated.",
    deleted: "Plan deactivated.",
    reactivated: "Plan reactivated.",
    failed: "Action failed: {{error}}",
    sitePlanLocked: "This plan is on the marketing site — it can’t be deleted.",
    subtitle: "Package definitions: numbers and price",
    price: "Price (₺)",
    priceHint: "Leave empty to sell by quote only (private/corporate package).",
    priceQuote: "By quote",
    priceDefault: "The default package (Free) is always ₺0.",
    sold: "packages sold",
    accountUsers: "{{n}} accounts on this legacy account plan",
    soldNote: "This package was sold {{n}} times. Changes apply to NEW sales only; sold packages keep the numbers they were bought with (contract). If a customer needs more, an upgrade is a separate sale.",
    updatedNewSalesOnly: "Plan updated · applies to new sales; {{n}} sold packages kept the numbers they were bought with.",
    errors: {
      defaultLocked: "The default (Free) package can’t be changed in the package model.",
      defaultPrice: "The default package is free (₺0).",
      priceZero: "A paid package can’t cost 0 — leave it empty (by quote) or enter a price.",
      migrationRequired: "Migration 059 is required for prices.",
      invalid: "Invalid value.",
    },
  },
  // ─── Admin: kullanıcı paketleri + Paket ver (2026-10-16) ─────────
  adminPackages: {
    title: "Packages",
    modeNote: "Package model: the account plan sets no limits; each wedding's limits come from its package. Give enterprise customers a custom-limit package.",
    grant: "Grant package",
    guests: "{{n}} guests",
    grantTitle: "Grant package",
    grantHint: "1 order = 1 package: a paid manual order is created. ALL of the package’s numbers come from its definition (Packages). The amount may be 0 or discounted; if it’s above 0 a payment reference is required. The reason goes to the internal note and the ledger. If you pick a wedding the package is assigned to it (Free / transition package ends); otherwise the user uses it from My packages.",
    plan: "Package",
    amount: "Amount (₺)",
    paymentNote: "Payment reference (required)",
    duplicateConfirm: "The same package was already granted to this user in the last 10 minutes. Grant a second one? (If it’s a second tab / retry, press Cancel.)",
    duplicateSkipped: "No second package granted — the one from the last 10 minutes stands.",
    revoke: "Revoke",
    revokePrompt: "Revoke the “{{label}}” package ({{wedding}})? Without another package the wedding is suspended (no new guests/uploads, nothing deleted). Reason (required):",
    revoked: "{{label}} package revoked.",
    wedding: "Wedding",
    weddingNone: "Don't assign — keep it available",
    weddingOption: "{{title}} — now: {{pkg}}",
    reason: "Reason",
    reasonPh: "e.g. phone sale, compensation, enterprise deal",
    submit: "Grant package",
    granted: "Package granted: {{number}}",
    grantedTo: "Package granted: {{number}} → {{title}}",
    packagesMode: "The account is on the package model — the account plan can't be changed; use “Grant package”.",
    errors: {
      packagesUnavailable: "Packages can't be granted before migration 058 is applied.",
      weddingNotFound: "The wedding doesn't belong to this user.",
      ordersUnavailable: "Orders table missing (migration 053/055).",
      generic: "Package could not be granted: {{error}}",
      paymentRef: "A payment reference (receipt / transfer description) is required when the amount is above 0.",
      targetHasPackage: "This wedding already has a paid package — one package per wedding.",
      packageNotFound: "Package definition not found or inactive.",
      packageNotSellable: "The default (Free) package can’t be granted — the account already has one.",
      reason: "A reason is required (at least 3 characters).",
      notActive: "The package has already ended or been revoked.",
      notFound: "The package doesn’t belong to this user.",
    },
  },

  // ─── Paketlerim (paket modeli, 2026-10-16) ─────────
  packages: {
    title: "My packages",
    subtitle: "Every wedding is tied to a package. Your account has one Free package; every paid order is a new package.",
    buy: "Buy a package",
    loadFailed: "Packages could not be loaded.",
    tileAvailable: "Available",
    tileInUse: "In use",
    tilePending: "Awaiting payment",
    tabPackages: "Packages",
    tabOrders: "Orders",
    available: "Available",
    inUse: "In use",
    pending: "Awaiting payment",
    endedTitle: "Ended",
    noneAvailable: "You have no available package. Buy one for a new wedding.",
    limits: "{{guests}} guests · {{media}} photos/videos · {{storage}}",
    freeName: "Free",
    legacyName: "{{plan}} (transition)",
    freeHint: "Once per account. Used up when the wedding is upgraded to a paid package.",
    paidHint: "The service starts when it is assigned to a wedding.",
    pendingHint: "Ordered: {{date}}. Bank transfer instructions are in your email — put the order number in the reference. The package becomes available once payment is confirmed.",
    ended: {
      replaced: "Replaced by a paid package",
      revoked: "Revoked",
      cancelled: "Cancelled / refunded",
    },
    openWedding: "Open a wedding with this package",
    orderUnassigned: "Not assigned to a wedding yet",
    orderPendingShort: "Awaiting payment",
    addonsTitle: "QR extra guest packs",
    addonSeats: "+{{seats}} guests",
  },

  // ─── Guest limit + QR extra registration (shared card, 2026-10-15) ──────
  guestCapacity: {
    title: "Guest limit",
    breakdown: "Package limit {{limit}} · extra {{extra}} (QR only)",
    full: "Limit reached — new QR sign-ups are not accepted. Guests are told to contact the couple. Open an extra pack to extend it.",
    almost: "Almost full. Once full, new QR sign-ups are not accepted.",
    qrExtra: "Extra QR sign-ups",
    off: "Off",
    on: "On · +{{seats}}",
    packButton: "+{{seats}} guests · {{price}}",
    statusPending: "payment pending",
    statusPaid: "paid",
    due: "Due: {{amount}} (bank transfer — details by email)",
    withdraw: "Withdraw",
    cancel: "Cancel",
    withdrawConfirm: "Withdraw the +{{seats}} pack? Extra seats close; no charge.",
    cancelConfirmAdmin: "Cancel the +{{seats}} pack? Extra seats close at once (refund is on you if paid).",
    withdrawn: "+{{seats}} pack closed.",
    opened: "+{{seats}} extra guests opened.",
    confirmTitle: "+{{seats}} extra guests",
    confirmLimit: "The QR sign-up limit goes from {{from}} to {{to}}.",
    confirmQrOnly: "Extra seats are only for QR sign-ups; adding guests to the list manually stays within the package limit.",
    confirmBilling: "The pack opens IMMEDIATELY; a “payment pending” order of {{price}} is created, payable by bank transfer.",
    confirmUnpaid: "If unpaid within {{days}} days the pack is cancelled automatically and extra sign-ups stop (registered guests are kept). Only one unpaid pack can be open at a time.",
    confirmWithdraw: "You can withdraw the pack for free as long as nobody has signed up by QR since it opened.",
    comp: "Complimentary (free)",
    pendingExists: "There’s an unpaid extra pack — a new one can’t be opened until it’s paid (or withdrawn).",
    compReason: "Reason for the free pack (required)",
    compReasonPh: "e.g. compensation, corporate deal",
    accept: "I have read and accept the price and terms.",
    confirmButton: "Open +{{seats}} guests",
    close: "Cancel",
    loadFailed: "Couldn’t load the guest limit.",
    retry: "Try again",
    migrationHint: "Extra packs need migration 056 (the limit rule still applies).",
    errors: {
      unavailable: "Extra packs can’t be opened right now.",
      disabled: "Extra guest packs are currently off.",
      unknownPack: "This pack is no longer offered — refresh the page.",
      duplicate: "This pack was just opened — refresh the page.",
      inUse: "This pack’s seats are in use; it can’t be withdrawn.",
      paid: "A paid pack can’t be withdrawn — please contact support.",
      generic: "Action failed: {{error}}",
      pendingExists: "There’s an unpaid extra pack — pay or withdraw it first.",
      suspended: "The wedding has no package (suspended) — extra packs can’t be opened.",
      reason: "A reason is required for a free pack.",
    },
    pkg: {
      title: "Package",
      order: "order {{number}}",
      account: "account-wide plan",
      trial: "free trial — no package",
      media: "Photos/videos",
      storage: "Storage",
      sms: "SMS",
      free: "Free package",
      legacy: "transition package (old account plan)",
      suspended: "no package — wedding suspended",
    },
  },

  planChange: {
    accountPlanWarning: "The account plan applies indefinitely to ALL weddings without an assigned package (account-wide). Pro and Max are sold per wedding: they cannot be assigned here — open a “Manual order” under Sales & Site → Online sales (use Quantity for several packages). The account plan is only for returning to Free and for custom (enterprise/subscription) agreements. For an old Pro/Max account, use the “Account plan → wedding packages” tool below.",
    title: "Change account plan",
    rowLabel: "Account plan",
    perWeddingOption: "Sold per wedding — via “Manual order”",
    perWeddingPlan: "Pro/Max cannot be assigned as an account plan — they are sold per wedding. Use Sales & Site → Online sales → “Manual order”.",
    conversion: {
      title: "Account plan → wedding packages",
      desc: "This account was given the {{plan}} account plan before the “one package = one wedding” model: every wedding without a package runs on {{plan}} limits and has no order number. Converting opens a 0 ₺ transition order for each wedding you select (number starting with G, recorded in the ledger); the wedding loses no limits. The account drops to the {{to}} (free trial) plan.",
      notConvertible: "This account's plan ({{plan}}) is a custom plan: it intentionally stays account-wide (enterprise). It is not converted.",
      guests: "{{count}} guests",
      hasOrder: "Has package: {{number}}",
      notOwner: "Owned by another account",
      archived: "archived",
      unselectedWarn: "{{count}} unselected wedding(s) drop to free-trial limits ({{guests}} guests) — no data is deleted, but new sign-ups and uploads above the limit stop. Leave test weddings unselected.",
      button: "Convert to wedding packages",
      confirm: "A {{plan}} transition package will be opened for {{count}} wedding(s) and the account will drop to the {{to}} plan. This cannot be undone (orders can be cancelled one by one if needed). Continue?",
      done: "Converted — {{count}} transition order(s): {{numbers}}",
      doneNone: "Converted — account dropped to {{to}}, no packages opened",
      failed: "Could not convert: {{error}}",
      planChanged: "The account's plan changed in the meantime — reload the page and try again",
      loadFailed: "Could not load conversion info: {{error}}",
    },
  },

  privacyAdmin: {
    title: "Privacy requests (KVKK)",
    description: "When someone asks for their data to be deleted: find their guest records across all weddings by phone number, preview them and delete/anonymize in one go. Searches and deletions are written to the audit log (with the number masked).",
    phoneLabel: "Phone number",
    phoneHint: "Type it as 0555 123 45 67 or +44….",
    willSearch: "Will search: {{phone}}",
    invalidPhone: "Enter a valid phone number.",
    search: "Find",
    lookupFailed: "Search failed: {{error}}",
    resultTitle: "Found for {{phone}}",
    resultMeta: "Verification codes: {{authCodes}} · Quote requests: {{leads}} · Audit entries: {{audit}}",
    ordersKept: "There are {{count}} orders for this number — kept for 10 years as invoice records under tax law; this tool does not delete them.",
    organizerNote: "An organizer account is also registered with this number. This tool does not delete organizer accounts; close it separately if needed:",
    nothing: "No records found for this number.",
    noGuests: "No guest records; only codes/quote requests/audit entries will be cleaned up.",
    noName: "(no name)",
    guestActive: "Active guest",
    guestRemoved: "Removed from the wedding",
    counts: "{{media}} media · {{comments}} comments · {{likes}} likes · {{rsvp}} RSVP · {{sms}} SMS",
    eraseExplain: "Deleted: the guest's photos/videos (with their files), comments and likes, RSVP, verification codes, quote requests and open sessions. The guest record is anonymized (name and phone removed); the number is masked in SMS send records and the audit log. This can't be undone.",
    erase: "Delete and anonymize",
    eraseConfirm: "{{count}} guest records for {{phone}} and all related data will be permanently deleted. This can't be undone. Continue?",
    erased: "Deletion complete",
    eraseFailed: "Could not delete: {{error}}",
    erasedSummary: "{{phone}}: {{guests}} guest records anonymized, {{media}} media deleted, number masked in {{audit}} audit entries.",
  },

  landing: {
    meta: {
      title: "{{brand}} — Every moment you missed, in one album",
      description: "Guests scan a QR code; every photo and video they take lands in one album in original quality, and the ones you approve play on the big screen at the venue. No app to download.",
    },
    nav: {
      home: "{{brand}} home",
      how: "How it works",
      sahne: "Sahne",
      themes: "Invitations",
      plans: "Plans",
      faq: "FAQ",
      contact: "Quote",
      login: "Sign in",
      cta: "Get a quote",
      menu: "Open menu",
      close: "Close menu",
      skip: "Skip to content",
    },
    hero: {
      eyebrow: "The QR wedding album",
      titleA: "Every moment you missed,",
      titleB: "in one album.",
      lead: "Guests scan a QR code and their photos and videos land in your album in original quality. The ones you approve play on the big screen all night long.",
      ctaPrimary: "Get a quote",
      ctaHow: "See how it works",
      live: "Live",
      live1: "New shot from table 7",
      live2: "Elif shared a video",
      live3: "14 hearts for the first dance",
      promise1: "No app — scanning the QR is enough",
      promise2: "Original quality, no compression",
      promise3: "Approved shots hit the big screen in seconds",
    },
    marquee: {
      m1: "Ceremony",
      m2: "Henna night",
      m3: "Wedding",
      m4: "Engagement",
      m5: "After party",
      m6: "Promise",
    },
    manifesto: {
      eyebrow: "Sound familiar?",
      body: "The morning after the wedding, the best shots of the night are scattered across forty-seven phones, twelve WhatsApp groups and one “I’ll send them later” that never comes.",
      resolve: "We bring them together in a single album, at the quality they were taken.",
      chip1: "“Can you send me the photos?”",
      chip2: "WhatsApp · compressed",
      chip3: "This link has expired",
      chip4: "“I’ll send them later” — Cousin Emre",
      chip5: "Storage full",
      chip6: "No access to the Drive folder",
    },
    how: {
      eyebrow: "How it works",
      title: "Three steps.",
      titleAccent: "Zero apps.",
      lead: "As familiar as a phone camera for your guests. A single album for you.",
      step1Title: "Scan",
      step1Body: "Guests scan the QR code on the table card, the invitation or the welcome sign with their phone camera. No downloads, no passwords.",
      step2Title: "Share",
      step2Body: "They sign in with a one-time code sent to their phone and upload photos and videos. No compression — exactly what they shot.",
      step3Title: "Watch",
      step3Body: "Every shot you approve lands on the big screen at the venue (Sahne) — with hearts sent from guests’ phones.",
      cardShare: "Share your memories with us",
      cardTable: "Table 7",
      cardScan: "Scan with your camera",
      phoneTitle: "Share a memory",
      phoneVerified: "Your number is verified",
      phoneUploading: "Uploading 4 files",
      phoneOriginal: "Original quality",
      phoneSend: "Share",
      tryAsGuest: "Try it as a guest",
      tryAsGuestNote: "Sample wedding — no phone number needed; uploads are deleted every night.",
    },
    sahne: {
      eyebrow: "Sahne",
      title: "The night’s shots,",
      titleAccent: "instantly",
      titleEnd: "on the big screen.",
      lead: "Guests shoot, you approve, and the shot is on the big screen within seconds. Hearts sent from phones float across it, comments become bubbles.",
      live: "Live",
      watching: "{{count}} watching",
      sharedBy: "Shared by {{name}}",
      comment1: "This dance is legendary 💃",
      comment2: "Look at the bride’s smile ❤️",
      comment3: "I’m joining the halay!",
      join: "Join the celebration",
      joinHint: "Share photos & send hearts",
      f1Title: "On screen in seconds",
      f1Body: "The moment you approve, the shot appears on every screen in the hall at once.",
      f2Title: "Hearts and comments from phones",
      f2Body: "Guests react with a single tap; the corner of the screen shows who is watching.",
      f3Title: "You control the screen",
      f3Body: "Pick the next shot from your phone — spotlight it, pause or hide it.",
      rejiLabel: "Reji",
      rejiNext: "Up next",
      rejiSpotlight: "Spotlight",
      videoCaption: "From a real wedding: Sahne through the night",
    },
    control: {
      eyebrow: "Control",
      title: "You are the editor",
      titleAccent: "of the night.",
      lead: "You decide what enters the album and what plays on screen. The dashboard handles the rest.",
      modTitle: "Approval before it goes live",
      modBody: "Every shot lands in the moderation queue first. Approve, reject — and if needed, block the sender with a single tap.",
      modPending: "{{count}} shots awaiting approval",
      modApprove: "Approve",
      modReject: "Reject",
      otpTitle: "Only real guests",
      otpBody: "Sign-in uses a one-time code sent to the guest’s phone. Your album is not a public link.",
      otpLabel: "Verification code",
      rsvpTitle: "RSVPs, in real time",
      rsvpBody: "Who’s coming, who isn’t, who’s still deciding — live on your dashboard.",
      rsvpYes: "Attending",
      rsvpNo: "Declined",
      rsvpPending: "No reply",
      smsTitle: "SMS invitations and reminders",
      smsBody: "An invitation for everyone, a gentle reminder for those who haven’t replied. Pick the audience, schedule it.",
      smsPreview: "Dear Ayşe Yılmaz, we’d be delighted to have you with us at Deniz & Arda’s wedding on August 23. Tap the link to let us know if you can attend.",
      smsSegment: "No RSVP yet",
      qrTitle: "QR analytics",
      qrBody: "Is your invitation actually being opened? See how many people scanned it, day by day.",
      qrWeek: "Last 7 days",
      eventsTitle: "Henna + Wedding",
      eventsBody: "Two events, one album. Once the first has passed, the countdown and venue switch to the next on their own.",
      eventsKina: "Henna",
      eventsDugun: "Wedding",
      eventsDone: "Done",
      langTitle: "Four languages",
      langBody: "Turkish, English, French, Spanish — every guest picks their own.",
      qualityTitle: "Original quality",
      qualityBody: "No compression. Whatever is on the guest’s phone is what lands in the album.",
      qualityPhoto: "per photo",
      qualityVideo: "per video",
      notifyTitle: "Telegram alerts",
      notifyBody: "Get a ping the moment a new shot is waiting for approval.",
      notifySample: "3 new photos awaiting approval",
    },
    themes: {
      eyebrow: "Invitation",
      title: "Your invitation isn’t a link.",
      titleAccent: "It’s an opening scene.",
      lead: "The first screen a guest sees after scanning: cinematic themes that unfold as they scroll. Preview each one right now.",
      count: "{{count}} themes",
      preview: "Preview",
      drag: "Drag to browse",
      premium: "Premium",
      classic: "Classic",
    },
    compare: {
      eyebrow: "Comparison",
      title: "Why a WhatsApp group",
      titleAccent: "isn’t enough.",
      feature: "Feature",
      colWhatsapp: "WhatsApp group",
      colDrive: "Drive folder",
      colQrAlbum: "Typical QR album",
      rowQuality: "Original quality",
      rowSingle: "Everything in one place",
      rowLive: "On the big screen all night",
      rowModeration: "Approval before it goes live",
      rowVerified: "Verified guests only",
      rowNoApp: "Nothing extra for guests to install",
      rowVideo: "Video",
      yes: "Yes",
      no: "No",
      partial: "Partly",
    },
    proof: {
      eyebrow: "Couples say",
      title: "Real weddings,",
      titleAccent: "real moments.",
      statWeddings: "wedding albums",
      statMedia: "photos and videos shared",
      rating: "{{n}} out of 5",
    },
    checkout: {
      eyebrow: "Buy online",
      title: "Buy your package",
      lead: "Pick your package, enter your details and confirm the order. Payment is by bank transfer; your account opens once it arrives.",
      loading: "Loading",
      closedTitle: "Online sales are closed right now",
      closedBody: "You can request a package through the quote form in two minutes; we reply the same day.",
      closedCta: "Go to the quote form",
      packageLabel: "Package",
      packageGuests: "up to {{n}} guests",
      packageQuote: "By quote",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      eventDate: "Event date",
      address: "Billing address",
      addressHint: "Full address for the contract and invoice.",
      taxId: "National ID / tax no.",
      taxIdHint: "For the invoice; use your company tax number if buying for a company.",
      optional: "optional",
      docsTitle: "Pre-contract information and distance sales contract",
      docsLangNote: "The contract texts are in Turkish (governed by Turkish law).",
      accept: "I have read and accept the pre-contract information form and the distance sales contract.",
      summaryTitle: "Order summary",
      summaryOnce: "One-time · all taxes included",
      summaryPick: "Pick your package.",
      step1: "1 — You confirm the order.",
      step2: "2 — We email you the payment details within 1 business day.",
      step3: "3 — Once paid, your account opens and the sign-in details arrive by email.",
      submit: "Confirm order",
      sending: "Sending…",
      seller: "Seller: {{name}}",
      errField: "Check this field.",
      errAccept: "Accept the contract texts to continue.",
      errInvalid: "Some fields are missing or invalid.",
      errPackage: "Pick a package that can be bought online.",
      errVerify: "Waiting for the security check — try again in a few seconds.",
      errVerifyFailed: "The security check could not load. Refresh the page or use the quote form.",
      errClosed: "This package isn't sold online right now — use the quote form.",
      errUnavailable: "Orders can't be taken right now. Please use the quote form.",
      errTermsChanged: "The price or contract terms were updated while you were reading. Please review the current text and confirm again.",
      errFailed: "The order could not be sent. Please try again.",
      toastSent: "Order received",
      successTitle: "Order received",
      successBody: "Your order number is {{number}}. We'll email the payment details to {{email}} within 1 business day.",
      successNext: "Once your payment reaches us, your organizer account opens; the sign-in details and a copy of the contracts arrive by email.",
      backHome: "Back to home",
    },
    plans: {
      eyebrow: "Plans",
      title: "Three packages,",
      titleAccent: "sized to your guest list.",
      lead: "Packages differ by guests, photos/videos and storage. We’ll help you pick the right one.",
      freeTagline: "For trying it out and small gatherings",
      proTagline: "For most weddings",
      maxTagline: "For big weddings and planners",
      limitGuests: "Guests",
      guestsUpTo: "up to {{n}}",
      limitMedia: "Photos and videos / wedding",
      limitStorage: "Storage",
      recommended: "Recommended",
      ctaFree: "Request the free package",
      ctaPaid: "Get a quote",
      ctaBuy: "Buy now",
      note: "Pricing depends on your events and guest count — just fill in the form.",
      priceOnce: "one-time",
      priceTrial: "free trial",
      priceQuote: "On request",
      customLine: "Planning a bigger event?",
      customCta: "Get a quote for a custom package",
      tierGuests: "{{range}} guests",
      priceNote: "One-time payment, no subscription. Henna night and wedding in one price; table card and welcome sign design included.",
      retention: "Your album stays online for at least {{months}} months; download all of it any time.",
    },
    print: {
      eyebrow: "Print",
      title: "Ready for the tables.",
      titleAccent: "Every surface, an invitation.",
      lead: "Print your QR code in your wedding’s own style. From table cards to welcome signs, download and explore our sample designs.",
      tableCard: "Table card",
      tableCardSize: "A6 · 105 × 148 mm",
      sign: "Welcome sign",
      signSize: "50 × 70 cm",
      handCard: "Hand card",
      handCardSize: "85 × 55 mm",
      download: "Sample PDF",
      sampleNote: "Sample designs — the QR code opens a sample invitation page. Versions printed with your own QR will come from the organizer dashboard.",
    },
    contact: {
      eyebrow: "Quote",
      title: "Get your price",
      titleAccent: "in 30 seconds.",
      lead: "Pick your guest count and date, tell us how to reach you — we’ll send your personal quote shortly.",
      point1: "Henna night, ceremony and wedding in one quote",
      point2: "Table card and sign design included",
      point3: "No sales pressure — just an answer",
      directLabel: "Prefer email over a form?",
      directCta: "Write to us",
      step1: "Your event",
      step2: "How can we reach you?",
      stepCount: "Step {{n}} of 2",
      next: "Continue",
      back: "Back",
      addNote: "Add a note",
      name: "Full name",
      namePh: "Ayşe Yılmaz",
      email: "Email",
      emailPh: "you@example.com",
      phone: "Phone",
      eventType: "Event",
      eventDate: "Event date",
      package: "Package",
      packageHint: "Pick the package that fits your guest count.",
      packageGuests: "up to {{n}} guests",
      packageCustom: "Bigger · custom quote",
      estimate: "Estimated price",
      message: "Your message",
      messagePh: "Venue, time, anything on your mind…",
      optional: "optional",
      evWedding: "Wedding",
      evWeddingHenna: "Wedding + Henna",
      evHenna: "Henna night",
      evEngagement: "Engagement",
      evPromise: "Promise",
      evOther: "Other",
      rangeUpTo: "Up to {{n}}",
      rangeBetween: "{{min}}–{{max}}",
      rangeOver: "{{n}}+",
      consent: "I give my explicit consent to my personal data being transferred to the service providers abroad listed in the Privacy Notice (email and hosting) so that my quote request can be delivered.",
      submit: "Get my quote",
      sending: "Sending…",
      successTitle: "Your request has reached us.",
      successBody: "We’ll get back to you shortly at {{email}} or by phone.",
      successAgain: "Send another request",
      errName: "Please enter your first and last name.",
      errEmail: "Enter a valid email address.",
      errPhone: "Enter a valid phone number.",
      errChoice: "Please choose an option.",
      errDate: "Choose a date from today onwards.",
      errConsent: "Please give your consent to continue.",
      errVerify: "The security check hasn’t finished yet; please try again in a few seconds.",
      toastSent: "Your request has been sent.",
      toastFailed: "Couldn’t send — you can send your request by email instead.",
      toastInvalid: "Please fix the highlighted fields.",
      fallbackTitle: "The form couldn’t be sent right now.",
      fallbackBody: "Your details are filled into a ready email; send it with a single tap.",
      fallbackCta: "Send by email",
      mailSubject: "Quote request — {{name}} · {{event}} · {{date}}",
      mailGreeting: "Hello,",
      mailIntro: "I’d like a quote for {{brand}}.",
      mailMessage: "Message",
      mailPlan: "Package",
      mailClosing: "Thank you.",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Good questions",
      q1: "Do guests need to download an app?",
      a1: "No. Scanning the QR code with the phone camera is enough; everything opens in the browser. Guests can add it to their home screen and use it like an app if they like.",
      q2: "Are photos compressed?",
      a2: "No. Files are uploaded exactly as they are on the guest’s phone — up to {{photo}} per photo and {{video}} per video.",
      q3: "Who can see the album?",
      a3: "Only guests who sign in by verifying their phone number — and they only see the shots you’ve approved.",
      q4: "Could an inappropriate shot end up on screen?",
      a4: "No. Every shot lands in the moderation queue first; Sahne only plays what you’ve approved. If needed, you can block the sender with a single tap.",
      q5: "Do I need separate albums for the henna night and the wedding?",
      a5: "No. You add both events to the same wedding; once the first has passed, the countdown and venue details switch to the second on their own.",
      q6: "What do I need for Sahne?",
      a6: "A screen or projector connected to the internet, and a browser. Open Sahne in full screen and run the flow from Reji on your phone.",
      q7: "Some of my guests are from abroad — is that a problem?",
      a7: "Not at all. The interface is available in Turkish, English, French and Spanish; every guest picks their own language.",
      q8: "How long does the album stay online?",
      a8: "Your album stays online for at least {{months}} months. During that time you can download every approved photo and video in original quality as a ZIP, in one click from your panel.",
    },
    finale: {
      title1: "Before the night ends,",
      title2: "the best shots",
      title3: "are on screen.",
      lead: "Get your quote — your QR code will be ready for the tables.",
      cta: "Get a quote",
      secondary: "Open a sample invitation",
    },
    footer: {
      tagline: "Every moment you missed, in one album.",
      product: "Product",
      account: "Account",
      organizerLogin: "Organizer sign-in",
      quote: "Get a quote",
      demo: "Sample invitation",
      rights: "All rights reserved.",
    },
  },
  weddingTelegram: {
    title: "Telegram notifications",
    subtitle: "This wedding's events go to the Telegram chats you choose: your own chat or the couple's / team's group.",
    unavailable: "Telegram notifications can't be set up right now (a system update is pending).",
    botMissing: "The Telegram bot isn't configured — let the administrator know.",
    loadFailed: "Couldn't load Telegram connections.",
    retry: "Try again",
    connectPrivate: "Connect my Telegram",
    connectPrivateHint: "Notifications come only to you.",
    connectGroup: "Add to a group",
    connectGroupHint: "The couple's or team's group — you pick the group in Telegram.",
    waitingPrivate: "Open Telegram and tap “Start”. It appears here once connected.",
    waitingGroup: "Pick the group in Telegram and add the bot. It appears here once connected.",
    openTelegram: "Open in Telegram",
    expiresIn: "Link valid for {{time}}",
    expired: "The link has expired. Create a new one.",
    cancel: "Cancel",
    linked: "Telegram connected: {{name}}",
    linkFailed: "Couldn't create the link",
    chatsTitle: "Connected chats",
    empty: "No connected chats yet.",
    private: "Personal",
    group: "Group",
    unnamedPrivate: "Personal chat",
    unnamedGroup: "Group",
    eventsLabel: "Notification types for {{name}}",
    remove: "Remove",
    removeConfirm: "“{{name}}” will stop receiving this wedding's notifications. Remove it?",
    removed: "Chat removed",
    saved: "Notification choice saved",
    saveFailed: "Couldn't save",
    stale: "The choice was changed elsewhere — the latest version is loaded.",
    footer: "If you block the bot or remove it from the group, the connection goes away on its own. /status in the chat lists connected weddings.",
    events: {
      media_upload: "New media (awaiting review)",
      guest_join: "New guest",
      rsvp_submitted: "RSVP reply",
      comment_created: "Comment",
      capacity: "Capacity alerts",
    },
    errors: {
      migration_required: "Telegram notifications can't be set up right now (a system update is pending).",
      bot_unavailable: "Couldn't reach the Telegram bot, try again shortly.",
      generic: "Error: {{error}}",
    },
  },

} as const;

export default en;
